import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCreatorStore, DesignTemplate, SavedDesign, TemplateElement, VideoExportRequest, DesignStatus } from '../store/creator';
import { useProjectsStore } from '../store/projects';
import { useMediaStore } from '../store/media';
import { useFlightsStore } from '../store/flights';
import { usePostsStore } from '../store/posts';
import { CanvasEditor, CanvasElement, CanvasSettings } from '../components/CanvasEditor';
import { ElementProperties } from '../components/ElementProperties';
import { ColorPicker, GradientPicker } from '../components/ColorPicker';
import { MediaPickerModal } from '../components/MediaPicker';
import { AIBriefingPanel } from '../components/AIBriefingPanel';
import { Breadcrumb } from '../components/Breadcrumb';
import { ElementLibraryPro } from '../components/ElementLibraryPro';
import { type IconWeight } from '../config/phosphorIcons';
import { LibraryElement } from '../config/elementLibrary';
import { CANVAS_PRESETS } from '../config/metaFormats';
import { getMediaUrl } from '../services/api';

// CreatorIcon - matches header navigation (paint/brush icon)
function CreatorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  );
}

// Debounce-Hook für Live-Vorschau
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Hook to ensure fonts are loaded before rendering
function useFontsReady(): boolean {
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    // Check if document.fonts API is available
    if ('fonts' in document) {
      document.fonts.ready.then(() => {
        setFontsReady(true);
      });
    } else {
      // Fallback: assume fonts are ready after a short delay
      const timer = setTimeout(() => setFontsReady(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return fontsReady;
}

// Sidebar: Template-Auswahl
function TemplateSidebar({
  templates,
  selectedTemplate,
  isLoading,
  categoryFilter,
  onCategoryChange,
  onSelectTemplate,
  onNewDesign,
}: {
  templates: DesignTemplate[];
  selectedTemplate: DesignTemplate | null;
  isLoading: boolean;
  categoryFilter: string;
  onCategoryChange: (category: string) => void;
  onSelectTemplate: (template: DesignTemplate | null) => void;
  onNewDesign: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('creator:templates')}</h2>
        <select
          value={categoryFilter}
          onChange={(e) => onCategoryChange(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
        >
          <option value="">{t('creator:allCategories')}</option>
          <option value="carousel">{t('creator:formats.carousel')}</option>
          <option value="post">{t('creator:formats.post')}</option>
          <option value="story">{t('creator:formats.story')}</option>
          <option value="quote">{t('creator:quote')}</option>
        </select>
      </div>

      <div className="flex-1 p-4 overflow-y-auto">
        {/* Leeres Template - ruft jetzt handleNewDesign auf */}
        <button
          onClick={onNewDesign}
          className={`w-full text-left rounded-lg border-2 overflow-hidden transition-all mb-3 ${
            !selectedTemplate
              ? 'border-blue-500 ring-2 ring-blue-200'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="h-24 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <div className="p-3 bg-white">
            <h4 className="font-medium text-sm text-gray-900">{t('creator:emptyDesign')}</h4>
            <p className="text-xs text-gray-500">{t('creator:fromScratch')}</p>
          </div>
        </button>

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">{t('creator:loading')}</div>
        ) : (
          <div className="space-y-3">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className={`w-full text-left rounded-lg border-2 overflow-hidden transition-all ${
                  selectedTemplate?.id === template.id
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div
                  className="h-24 flex items-center justify-center"
                  style={{
                    background: template.canvas.background_type === 'gradient'
                      ? template.canvas.background_value
                      : template.canvas.background_value,
                  }}
                >
                  <span className="text-gray-400 text-xs">
                    {template.canvas.width} x {template.canvas.height}
                  </span>
                </div>
                <div className="p-3 bg-white">
                  <h4 className="font-medium text-sm text-gray-900 truncate">{template.name}</h4>
                  <p className="text-xs text-gray-500">{template.category}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Hintergrund-Editor
function BackgroundEditor({
  canvas,
  onChange,
}: {
  canvas: CanvasSettings;
  onChange: (canvas: CanvasSettings) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="p-4 space-y-4">
      <h3 className="font-medium text-gray-900">{t('creator:background')}</h3>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:type')}</label>
        <select
          value={canvas.background_type}
          onChange={(e) => onChange({
            ...canvas,
            background_type: e.target.value as 'color' | 'gradient' | 'image',
            background_value: e.target.value === 'color' ? '#FFFFFF' :
                             e.target.value === 'gradient' ? 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' :
                             '',
          })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="color">{t('creator:color')}</option>
          <option value="gradient">{t('creator:gradient')}</option>
          <option value="image">{t('creator:image')}</option>
        </select>
      </div>

      {canvas.background_type === 'color' && (
        <ColorPicker
          color={canvas.background_value}
          onChange={(color) => onChange({ ...canvas, background_value: color })}
          label={t('creator:backgroundColor')}
          allowTransparent
        />
      )}

      {canvas.background_type === 'gradient' && (
        <GradientPicker
          value={canvas.background_value}
          onChange={(gradient) => onChange({ ...canvas, background_value: gradient })}
          label={t('creator:gradient')}
        />
      )}

      {canvas.background_type === 'image' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:imageUrl')}</label>
          <input
            type="text"
            value={canvas.background_value}
            onChange={(e) => onChange({ ...canvas, background_value: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="https://..."
          />
        </div>
      )}
    </div>
  );
}

// Hauptkomponente
export default function CreatorStudio() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Ensure fonts are loaded before rendering canvas
  const fontsReady = useFontsReady();
  const { t } = useTranslation();

  const {
    templates,
    selectedTemplate,
    savedDesigns,
    llmTemplates,
    editingLLMTemplateId,
    isLoading,
    isGenerating,
    isSaving,
    isExportingVideo,
    ffmpegAvailable,
    lastVideoExport,
    error,
    fetchTemplates,
    selectTemplate,
    fetchSavedDesigns,
    getSavedDesign,
    fetchLLMTemplates,
    createLLMTemplate,
    updateLLMTemplate,
    deleteLLMTemplate,
    importLLMTemplate,
    createDesignsFromTemplate,
    setEditingLLMTemplateId,
    saveDesign,
    updateDesign,
    deleteDesign,
    loadDesign,
    generatePreview,
    checkFfmpegAvailable,
    exportVideo,
    clearError,
  } = useCreatorStore();

  const { projects, selectedProjectId, fetchProjects, fetchProject, setSelectedProjectId } = useProjectsStore();
  const { fetchMedia } = useMediaStore();

  // Check for project and design query parameters
  const queryProjectId = searchParams.get('project');
  const queryDesignId = searchParams.get('design');

  // Use URL projectId if available, then query param, otherwise use global selectedProjectId
  const projectId = urlProjectId || queryProjectId || selectedProjectId;

  // Fetch projects if not loaded yet
  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects();
    }
  }, [projects.length, fetchProjects]);

  // Set project from query parameter
  useEffect(() => {
    if (queryProjectId && projects.length > 0) {
      const projectExists = projects.some(p => p.id === queryProjectId);
      if (projectExists) {
        setSelectedProjectId(queryProjectId);
      }
    }
  }, [queryProjectId, projects, setSelectedProjectId]);

  // Track if we've already loaded from URL to prevent duplicate loads
  const [loadedDesignIdFromUrl, setLoadedDesignIdFromUrl] = useState<string | null>(null);

  // Auto-select first project if none selected
  useEffect(() => {
    if (!projectId && projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projectId, projects, selectedProjectId, setSelectedProjectId]);

  // Handle project change from dropdown
  const handleProjectChange = (newProjectId: string) => {
    setSelectedProjectId(newProjectId);
  };

  // Get current project info
  const selectedProject = projects.find(p => p.id === projectId);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);

  // Filter projects based on search
  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return projects;
    const search = projectSearch.toLowerCase();
    return projects.filter(p =>
      p.name.toLowerCase().includes(search) ||
      p.display_id.toLowerCase().includes(search)
    );
  }, [projects, projectSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setShowProjectDropdown(false);
        setProjectSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSavedDesigns, setShowSavedDesigns] = useState(false);
  const [showVideoExportModal, setShowVideoExportModal] = useState(false);
  const [showElementLibrary, setShowElementLibrary] = useState(false);
  const [videoExportDuration, setVideoExportDuration] = useState<number>(10);

  // Save Modal State
  const [designName, setDesignName] = useState('');
  const [designDescription, setDesignDescription] = useState('');
  const [designCategory, setDesignCategory] = useState<'post' | 'story' | 'reel' | 'carousel'>('post');
  const [designPlatform, setDesignPlatform] = useState<'facebook' | 'instagram' | 'both'>('both');
  const [designTags, setDesignTags] = useState('');
  const [designFlightId, setDesignFlightId] = useState<string | null>(null);

  // Create Post option in Save Modal
  const [createPostAfterSave, setCreatePostAfterSave] = useState(false);
  const [postText, setPostText] = useState('');
  const [postDate, setPostDate] = useState('');
  const [isCreatingPost, setIsCreatingPost] = useState(false);

  // Separate "Create Post from Design" Modal
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [selectedDesignForPost, setSelectedDesignForPost] = useState<SavedDesign | null>(null);
  const [createPostText, setCreatePostText] = useState('');
  const [createPostDate, setCreatePostDate] = useState('');
  const [createPostFlightId, setCreatePostFlightId] = useState<string | null>(null);

  // Tracking current editing design (for updates)
  const [editingDesignId, setEditingDesignId] = useState<string | null>(null);

  // Canvas State
  const [canvas, setCanvas] = useState<CanvasSettings>({
    width: 1080,
    height: 1350,
    background_type: 'color',
    background_value: '#FFFFFF',
  });

  // Elements State
  const [elements, setElements] = useState<CanvasElement[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);

  // Preview State
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Dirty State für Speicher-Warnung
  const [isDirty, setIsDirty] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const lastSavedStateRef = useRef<string>('');

  // Undo/Redo History
  interface HistoryState {
    canvas: CanvasSettings;
    elements: CanvasElement[];
  }
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoRef = useRef(false);

  // Template Modal State
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState<'post' | 'story' | 'reel' | 'carousel'>('post');
  const [templateType, setTemplateType] = useState<'campaign' | 'editorial' | 'series' | 'evergreen' | 'seasonal'>('campaign');
  const [templateSaveResult, setTemplateSaveResult] = useState<{ success: boolean; message: string; displayId?: string } | null>(null);
  const [showLLMTemplates, setShowLLMTemplates] = useState(false);
  const [showAIBriefing, setShowAIBriefing] = useState(false);
  const [showContentImport, setShowContentImport] = useState<{ templateId: string; templateName: string } | null>(null);
  const [contentImportJson, setContentImportJson] = useState('');
  const [contentImportLoading, setContentImportLoading] = useState(false);

  // Flights for AI Briefing
  const { flights, fetchFlights } = useFlightsStore();

  // Posts store for creating posts from designs
  const { createPost } = usePostsStore();

  // Rechte Sidebar: Properties oder Background
  const [rightPanel, setRightPanel] = useState<'element' | 'background'>('background');

  // Debounced values für Live-Vorschau (1.5 Sekunden Verzögerung)
  const debouncedCanvas = useDebounce(canvas, 1500);
  const debouncedElements = useDebounce(elements, 1500);

  // Fetch templates on mount and when category changes
  useEffect(() => {
    fetchTemplates(categoryFilter || undefined);
  }, [fetchTemplates, categoryFilter]);

  // Fetch project-specific data when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchProject(projectId);
      fetchMedia(projectId);
      fetchSavedDesigns(projectId);
      fetchLLMTemplates(projectId);
      fetchFlights(projectId);
    }
  }, [projectId, fetchProject, fetchMedia, fetchSavedDesigns, fetchLLMTemplates, fetchFlights]);

  // Load design from query parameter (e.g., from Media library "Bearbeiten" button)
  useEffect(() => {
    if (queryDesignId && projectId && loadedDesignIdFromUrl !== queryDesignId) {
      // Mark as loading to prevent duplicate loads
      setLoadedDesignIdFromUrl(queryDesignId);

      // Load the design by ID
      getSavedDesign(queryDesignId).then((design) => {
        if (design) {
          // Elemente konvertieren (gleiche Logik wie handleLoadDesign)
          const editorElements: CanvasElement[] = [
            ...design.elements.map((el) => ({
              id: el.id,
              type: el.type === 'icon' ? 'audio' as const : el.type as 'text' | 'image' | 'shape' | 'group',
              position: el.position,
              size: el.size,
              style: el.style,
              content: el.content || '',
            })),
            ...((design as any).audio_tracks || []).map((el: any) => ({
              id: el.id,
              type: 'audio' as const,
              position: el.position,
              size: el.size,
              style: el.style,
              content: el.content || '',
            })),
          ];

          // WICHTIG: lastSavedStateRef ZUERST setzen, BEVOR State-Updates!
          // Sonst triggert der dirty-tracking useEffect bevor wir isDirty=false setzen
          lastSavedStateRef.current = JSON.stringify({ canvas: design.canvas, elements: editorElements });

          // Dirty State zurücksetzen BEVOR andere State-Updates
          setIsDirty(false);

          // Canvas und Elemente setzen
          setCanvas(design.canvas);
          setElements(editorElements);

          // Tracking setzen für Update
          setEditingDesignId(design.id);
          setDesignName(design.name);
          setDesignDescription(design.description || '');
          setDesignCategory(design.category || 'post');
          setDesignPlatform(design.platform || 'both');
          setDesignTags(design.tags?.join(', ') || '');
          setDesignFlightId(design.flight_id || null);

          // Reset state
          setSelectedElementId(null);
          setPreviewUrl(null);

          loadDesign(design);
        }
      }).catch((err) => {
        console.error('Failed to load design from URL:', err);
      });
    }
  }, [queryDesignId, projectId, loadedDesignIdFromUrl, getSavedDesign, loadDesign]);

  // Check ffmpeg availability once on mount
  useEffect(() => {
    if (ffmpegAvailable === null) {
      checkFfmpegAvailable();
    }
  }, [ffmpegAvailable, checkFfmpegAvailable]);

  // Check if current design has audio elements
  const hasAudioElements = elements.some(el => el.type === 'audio' && el.content);

  // Aktueller State als String für Dirty-Vergleich
  const currentStateString = useMemo(() => {
    return JSON.stringify({ canvas, elements });
  }, [canvas, elements]);

  // Dirty State tracken
  useEffect(() => {
    if (lastSavedStateRef.current && currentStateString !== lastSavedStateRef.current) {
      setIsDirty(true);
    }
  }, [currentStateString]);

  // Auto-Update der Vorschau bei Änderungen (debounced)
  useEffect(() => {
    // Nur aktualisieren wenn Vorschau aktiv ist und Elemente vorhanden
    if (previewUrl && debouncedElements.length > 0) {
      handleGeneratePreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCanvas, debouncedElements]);

  // Browser-Tab/Fenster schließen warnen + Auto-Save bei beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        // Session im localStorage speichern für Recovery
        const sessionData = {
          projectId,
          editingDesignId,
          designName,
          designDescription,
          designCategory,
          designPlatform,
          designTags,
          designFlightId,
          canvas,
          elements,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem('creator_session_backup', JSON.stringify(sessionData));

        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, projectId, editingDesignId, designName, designDescription, designCategory, designPlatform, designTags, designFlightId, canvas, elements]);

  // Auto-Save alle 5 Sekunden wenn dirty und bestehendes Design
  const autoSaveTimerRef = useRef<number | null>(null);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    // Nur Auto-Save wenn dirty, ein bestehendes Design bearbeitet wird, und projectId vorhanden
    if (!isDirty || !editingDesignId || !projectId) {
      return;
    }

    // Timer für Auto-Save (5 Sekunden nach letzter Änderung)
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = window.setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');

        // Convert elements to template elements (same logic as handleQuickSave)
        const templateElements = elements.map(el => ({
          id: el.id,
          type: el.type === 'audio' ? 'icon' as const : el.type, // Audio als icon speichern (Workaround)
          position: el.position,
          size: el.size,
          style: {
            ...el.style,
            // Audio-spezifische Eigenschaften im style speichern
            ...(el.type === 'audio' ? {
              audioUrl: el.content,
              audioVolume: (el as any).volume,
              audioLoop: (el as any).loop,
              audioFadeIn: (el as any).fadeInDuration,
              audioFadeOut: (el as any).fadeOutDuration,
              isAudioElement: true,
            } : {}),
          },
          content: el.type === 'audio' ? '🔊' : el.content, // Audio-Icon als Platzhalter
          lockAspectRatio: el.lockAspectRatio,
        }));

        const tagsArray = designTags
          .split(',')
          .map(t => t.trim())
          .filter(t => t.length > 0);

        await updateDesign(editingDesignId, {
          name: designName,
          description: designDescription || undefined,
          canvas,
          elements: templateElements,
          category: designCategory,
          platform: designPlatform,
          tags: tagsArray,
        });

        setIsDirty(false);
        lastSavedStateRef.current = JSON.stringify({ canvas, elements });
        setLastAutoSave(new Date());
        setAutoSaveStatus('saved');

        // Status nach 2 Sekunden zurücksetzen
        setTimeout(() => setAutoSaveStatus('idle'), 2000);

        // Session-Backup aus localStorage entfernen nach erfolgreichem Save
        localStorage.removeItem('creator_session_backup');
      } catch (err) {
        console.error('Auto-save failed:', err);
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      }
    }, 5000); // 5 Sekunden Verzögerung

    return () => {
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isDirty, editingDesignId, projectId, canvas, elements, designName, designDescription, designCategory, designPlatform, designTags, updateDesign]);

  // Auto-Save bei Tab-Wechsel (visibility change)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Wenn Tab versteckt wird und dirty ist, speichern
      if (document.hidden && isDirty && editingDesignId && projectId) {
        try {
          const templateElements = elements.map(el => ({
            id: el.id,
            type: el.type === 'audio' ? 'icon' as const : el.type,
            position: el.position,
            size: el.size,
            style: {
              ...el.style,
              ...(el.type === 'audio' ? {
                audioUrl: el.content,
                audioVolume: (el as any).volume,
                audioLoop: (el as any).loop,
                audioFadeIn: (el as any).fadeInDuration,
                audioFadeOut: (el as any).fadeOutDuration,
                isAudioElement: true,
              } : {}),
            },
            content: el.type === 'audio' ? '🔊' : el.content,
            lockAspectRatio: el.lockAspectRatio,
          }));

          const tagsArray = designTags
            .split(',')
            .map(t => t.trim())
            .filter(t => t.length > 0);

          await updateDesign(editingDesignId, {
            name: designName,
            description: designDescription || undefined,
            canvas,
            elements: templateElements,
            category: designCategory,
            platform: designPlatform,
            tags: tagsArray,
          });

          setIsDirty(false);
          lastSavedStateRef.current = JSON.stringify({ canvas, elements });
          setLastAutoSave(new Date());
          localStorage.removeItem('creator_session_backup');
        } catch (err) {
          console.error('Visibility change auto-save failed:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isDirty, editingDesignId, projectId, canvas, elements, designName, designDescription, designCategory, designPlatform, designTags, updateDesign]);

  // Session Recovery: Beim Start prüfen ob eine Session-Backup vorhanden ist
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [recoveryData, setRecoveryData] = useState<any>(null);

  useEffect(() => {
    const savedSession = localStorage.getItem('creator_session_backup');
    if (savedSession) {
      try {
        const data = JSON.parse(savedSession);
        // Nur anzeigen wenn weniger als 24 Stunden alt
        const savedAt = new Date(data.savedAt);
        const hoursAgo = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);
        if (hoursAgo < 24) {
          setRecoveryData(data);
          setShowRecoveryPrompt(true);
        } else {
          localStorage.removeItem('creator_session_backup');
        }
      } catch (e) {
        localStorage.removeItem('creator_session_backup');
      }
    }
  }, []);

  const handleRecoverSession = () => {
    if (recoveryData) {
      if (recoveryData.projectId) setSelectedProjectId(recoveryData.projectId);
      if (recoveryData.editingDesignId) setEditingDesignId(recoveryData.editingDesignId);
      if (recoveryData.designName) setDesignName(recoveryData.designName);
      if (recoveryData.designDescription) setDesignDescription(recoveryData.designDescription);
      if (recoveryData.designCategory) setDesignCategory(recoveryData.designCategory);
      if (recoveryData.designPlatform) setDesignPlatform(recoveryData.designPlatform);
      if (recoveryData.designTags) setDesignTags(recoveryData.designTags);
      if (recoveryData.designFlightId) setDesignFlightId(recoveryData.designFlightId);
      if (recoveryData.canvas) setCanvas(recoveryData.canvas);
      if (recoveryData.elements) setElements(recoveryData.elements);
      setIsDirty(true);
    }
    setShowRecoveryPrompt(false);
    localStorage.removeItem('creator_session_backup');
  };

  const handleDiscardRecovery = () => {
    setShowRecoveryPrompt(false);
    localStorage.removeItem('creator_session_backup');
  };

  // Debounced state for history (500ms delay - captures complete actions, not each pixel)
  const debouncedHistoryCanvas = useDebounce(canvas, 500);
  const debouncedHistoryElements = useDebounce(elements, 500);

  // History tracking für Undo/Redo - nur nach abgeschlossenen Aktionen (debounced)
  useEffect(() => {
    // Nicht tracken wenn es von Undo/Redo kommt
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }

    const newState: HistoryState = { canvas: debouncedHistoryCanvas, elements: debouncedHistoryElements };
    const newStateStr = JSON.stringify(newState);

    // Nur hinzufügen wenn sich etwas geändert hat
    if (history.length === 0 || JSON.stringify(history[historyIndex]) !== newStateStr) {
      // Alles nach dem aktuellen Index entfernen (bei neuer Aktion nach Undo)
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newState);

      // Maximal 50 History-Einträge behalten
      if (newHistory.length > 50) {
        newHistory.shift();
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      } else {
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedHistoryCanvas, debouncedHistoryElements]);

  // Undo Funktion
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoRef.current = true;
      const prevState = history[historyIndex - 1];
      setCanvas(prevState.canvas);
      setElements(prevState.elements);
      setHistoryIndex(historyIndex - 1);
      setSelectedElementId(null);
    }
  }, [history, historyIndex]);

  // Redo Funktion
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoRef.current = true;
      const nextState = history[historyIndex + 1];
      setCanvas(nextState.canvas);
      setElements(nextState.elements);
      setHistoryIndex(historyIndex + 1);
      setSelectedElementId(null);
    }
  }, [history, historyIndex]);

  // Wenn Template ausgewählt wird, lade dessen Canvas und Elemente
  // Bei null (Leeres Design) → Canvas zurücksetzen
  useEffect(() => {
    if (selectedTemplate) {
      setCanvas(selectedTemplate.canvas);
      // Konvertiere Template-Elemente zu Editor-Elementen
      const editorElements: CanvasElement[] = selectedTemplate.elements.map((el) => ({
        id: el.id,
        type: el.type === 'icon' ? 'text' : el.type,
        position: el.position,
        size: el.size,
        style: el.style,
        content: el.content || '',
      }));
      setElements(editorElements);
      setSelectedElementId(null);
      setPreviewUrl(null);
      // Design-Bearbeitung zurücksetzen
      setEditingDesignId(null);
      setDesignName('');
      setDesignDescription('');
    }
  }, [selectedTemplate]);

  // Neues leeres Design erstellen
  const handleNewDesign = useCallback(() => {
    // Canvas auf Standard zurücksetzen
    const newCanvas = {
      width: 1080,
      height: 1920,
      background_type: 'color' as const,
      background_value: '#ffffff',
    };
    setCanvas(newCanvas);
    setElements([]);
    setSelectedElementId(null);
    setSelectedElementIds([]);
    setPreviewUrl(null);
    // Design-Bearbeitung zurücksetzen
    setEditingDesignId(null);
    setDesignName('');
    setDesignDescription('');
    setDesignCategory('post');
    setDesignPlatform('both');
    setDesignTags('');
    // LLM-Template-Bearbeitung zurücksetzen
    setEditingLLMTemplateId(null);
    setTemplateName('');
    setTemplateDescription('');
    setTemplateCategory('post');
    setTemplateType('campaign');
    // Template-Auswahl zurücksetzen
    selectTemplate(null);
    // Dirty State zurücksetzen und neuen Zustand als "gespeichert" markieren
    setIsDirty(false);
    lastSavedStateRef.current = JSON.stringify({ canvas: newCanvas, elements: [] });
  }, [selectTemplate, setEditingLLMTemplateId]);

  // Selektiertes Element
  const selectedElement = elements.find((el) => el.id === selectedElementId);

  // Element hinzufügen
  const handleAddElement = useCallback((type: 'text' | 'image' | 'shape' | 'audio') => {
    // Bestimme Styles basierend auf Element-Typ
    const getDefaultStyle = () => {
      switch (type) {
        case 'text':
          return {
            fontFamily: 'Avenir Next',
            fontSize: 32,
            fontWeight: 'normal' as const,
            color: '#000000',
            textAlign: 'left' as const,
            verticalAlign: 'top' as const,
          };
        case 'shape':
          return {
            backgroundColor: '#3B82F6',
            shapeType: 'rectangle' as const,
            borderRadius: 0,
          };
        case 'audio':
          return {
            volume: 100,
            fadeInDuration: 0,
            fadeOutDuration: 0,
            audioLoop: false,
          };
        default: // image
          return {
            objectFit: 'cover' as const,
            borderRadius: 0,
          };
      }
    };

    // Berechne visuell symmetrische Größe für Shapes (visuelle 200x200px auf Canvas)
    // Ziel: Ein Shape soll bei gleichem Pixel-Wert für Breite und Höhe visuell quadratisch sein
    const getDefaultSize = () => {
      if (type === 'audio') {
        return { width: 20, height: 15 };
      }
      if (type === 'text') {
        return { width: 30, height: 15 };
      }
      if (type === 'shape') {
        // Ziel: 200px auf beiden Achsen (visuell quadratisch)
        const targetPx = 200;
        const widthPercent = (targetPx / canvas.width) * 100;
        const heightPercent = (targetPx / canvas.height) * 100;
        return { width: widthPercent, height: heightPercent };
      }
      // image
      return { width: 30, height: 20 };
    };

    const newElement: CanvasElement = {
      id: `element_${Date.now()}`,
      type,
      position: { x: 10, y: 10 },
      size: getDefaultSize(),
      style: getDefaultStyle(),
      content: type === 'text' ? t('creator:newText') : '',
    };

    setElements([...elements, newElement]);
    setSelectedElementId(newElement.id);
    setRightPanel('element');
  }, [elements, canvas.width, canvas.height]);

  // Library-Element hinzufügen (SVG aus Element-Bibliothek oder Phosphor Icon)
  const handleAddLibraryElement = useCallback((libraryElement: LibraryElement & { phosphorIcon?: string; phosphorWeight?: IconWeight }) => {
    // Berechne Größe basierend auf Library-Element oder Canvas
    const defaultWidth = libraryElement.defaultWidth || 100;
    const defaultHeight = libraryElement.defaultHeight || 100;

    // Zielgröße in Pixeln auf dem Canvas (ca. 150-200px)
    const targetPx = 180;
    const widthPercent = (targetPx / canvas.width) * 100;
    const heightPercent = ((targetPx * (defaultHeight / defaultWidth)) / canvas.height) * 100;

    // Für Phosphor Icons: SVG generieren
    let svgContent = libraryElement.svg;
    if (libraryElement.phosphorIcon) {
      // Wir speichern Phosphor-Icon Metadaten im Element für späteren Rendering
      svgContent = `<phosphor-icon name="${libraryElement.phosphorIcon}" weight="${libraryElement.phosphorWeight || 'regular'}" />`;
    }

    const newElement: CanvasElement = {
      id: `element_${Date.now()}`,
      type: 'shape', // SVG-Elemente werden als Shape mit svgContent behandelt
      position: { x: 35, y: 35 }, // Zentriert
      size: { width: widthPercent, height: heightPercent },
      style: {
        backgroundColor: libraryElement.defaultFill || '#3B82F6',
        shapeType: 'custom' as const,
        borderRadius: 0,
        // SVG-Content speichern
        svgContent: svgContent,
        libraryElementId: libraryElement.id,
        // Phosphor Icon Metadaten
        phosphorIcon: libraryElement.phosphorIcon,
        phosphorWeight: libraryElement.phosphorWeight,
      },
      content: libraryElement.name,
    };

    setElements([...elements, newElement]);
    setSelectedElementId(newElement.id);
    setRightPanel('element');
    setShowElementLibrary(false);
  }, [elements, canvas.width, canvas.height]);

  // Element aktualisieren
  const handleElementChange = useCallback((updatedElement: CanvasElement) => {
    setElements(elements.map((el) =>
      el.id === updatedElement.id ? updatedElement : el
    ));
  }, [elements]);

  // Element löschen
  const handleDeleteElement = useCallback(() => {
    if (!selectedElementId) return;
    setElements(elements.filter((el) => el.id !== selectedElementId));
    setSelectedElementId(null);
    setRightPanel('background');
  }, [selectedElementId, elements]);

  // Element duplizieren
  const handleDuplicateElement = useCallback(() => {
    if (!selectedElement) return;
    const newElement: CanvasElement = {
      ...selectedElement,
      id: `element_${Date.now()}`,
      position: {
        x: Math.min(selectedElement.position.x + 5, 90),
        y: Math.min(selectedElement.position.y + 5, 90),
      },
    };
    setElements([...elements, newElement]);
    setSelectedElementId(newElement.id);
  }, [selectedElement, elements]);

  // Medium aus MediaPicker auswählen
  const handleMediaSelect = useCallback((mediaUrl: string) => {
    if (!selectedElementId) return;
    setElements(elements.map((el) =>
      el.id === selectedElementId
        ? { ...el, content: mediaUrl }
        : el
    ));
    setShowMediaPicker(false);
  }, [selectedElementId, elements]);

  // Element nach vorne/hinten
  const handleMoveElement = useCallback((direction: 'up' | 'down') => {
    if (!selectedElementId) return;

    const index = elements.findIndex((el) => el.id === selectedElementId);
    if (index === -1) return;

    const newElements = [...elements];
    if (direction === 'up' && index < elements.length - 1) {
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
    } else if (direction === 'down' && index > 0) {
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
    }

    // Update zIndex basierend auf Array-Position
    setElements(newElements.map((el, i) => ({ ...el, style: { ...el.style, zIndex: i } })));
  }, [selectedElementId, elements]);

  // Multi-Selection State
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);

  // Elemente gruppieren
  const handleGroupElements = useCallback((elementIds: string[]) => {
    if (elementIds.length < 2) return;

    // Finde die Bounding Box aller Elemente
    const elementsToGroup = elements.filter(el => elementIds.includes(el.id));
    const minX = Math.min(...elementsToGroup.map(el => el.position.x));
    const minY = Math.min(...elementsToGroup.map(el => el.position.y));
    const maxX = Math.max(...elementsToGroup.map(el => el.position.x + el.size.width));
    const maxY = Math.max(...elementsToGroup.map(el => el.position.y + el.size.height));

    // Erstelle Gruppen-Element
    const groupId = `group_${Date.now()}`;
    const groupElement: CanvasElement = {
      id: groupId,
      type: 'group',
      position: { x: minX, y: minY },
      size: { width: maxX - minX, height: maxY - minY },
      style: { opacity: 1 },
      children: elementIds,
    };

    // Markiere Kind-Elemente mit groupId
    const newElements = elements.map(el =>
      elementIds.includes(el.id)
        ? { ...el, groupId }
        : el
    );

    setElements([...newElements, groupElement]);
    setSelectedElementId(groupId);
    setSelectedElementIds([groupId]);
  }, [elements]);

  // Gruppierung aufheben
  const handleUngroupElements = useCallback((groupId: string) => {
    const group = elements.find(el => el.id === groupId);
    if (!group || group.type !== 'group') return;

    // Entferne groupId von Kind-Elementen und lösche Gruppen-Element
    const newElements = elements
      .filter(el => el.id !== groupId)
      .map(el =>
        el.groupId === groupId
          ? { ...el, groupId: undefined }
          : el
      );

    setElements(newElements);
    setSelectedElementId(null);
    setSelectedElementIds([]);
  }, [elements]);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Nicht reagieren wenn in einem Input-Feld
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + Z = Undo
      if (modifier && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }

      // Cmd/Ctrl + Shift + Z oder Cmd/Ctrl + Y = Redo
      if ((modifier && e.key === 'z' && e.shiftKey) || (modifier && e.key === 'y')) {
        e.preventDefault();
        handleRedo();
        return;
      }

      // Cmd/Ctrl + D = Duplizieren
      if (modifier && e.key === 'd') {
        e.preventDefault();
        if (selectedElementId) {
          handleDuplicateElement();
        }
        return;
      }

      // Cmd/Ctrl + A = Alle auswählen
      if (modifier && e.key === 'a') {
        e.preventDefault();
        const allIds = elements.filter(el => el.type !== 'group').map(el => el.id);
        setSelectedElementIds(allIds);
        if (allIds.length > 0) {
          setSelectedElementId(allIds[0]);
        }
        return;
      }

      // Delete / Backspace = Löschen
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElementId) {
        e.preventDefault();
        handleDeleteElement();
        return;
      }

      // Escape = Auswahl aufheben
      if (e.key === 'Escape') {
        setSelectedElementId(null);
        setSelectedElementIds([]);
        return;
      }

      // Arrow Keys = Element verschieben (nur wenn Element ausgewählt)
      if (selectedElementId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1; // Größere Schritte mit Shift
        setElements(elements.map((el) => {
          if (el.id !== selectedElementId) return el;
          let { x, y } = el.position;
          switch (e.key) {
            case 'ArrowUp': y -= step; break;
            case 'ArrowDown': y += step; break;
            case 'ArrowLeft': x -= step; break;
            case 'ArrowRight': x += step; break;
          }
          return { ...el, position: { x, y } };
        }));
        return;
      }

      // Cmd/Ctrl + G = Gruppieren
      if (modifier && e.key === 'g' && !e.shiftKey && selectedElementIds.length > 1) {
        e.preventDefault();
        handleGroupElements(selectedElementIds);
        return;
      }

      // Cmd/Ctrl + Shift + G = Gruppierung aufheben
      if (modifier && e.key === 'g' && e.shiftKey && selectedElementId) {
        e.preventDefault();
        const element = elements.find(el => el.id === selectedElementId);
        if (element?.type === 'group') {
          handleUngroupElements(selectedElementId);
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleUndo, handleRedo, handleDuplicateElement, handleDeleteElement,
    selectedElementId, selectedElementIds, elements, handleGroupElements, handleUngroupElements
  ]);

  // Vorschau generieren
  const handleGeneratePreview = async () => {
    try {
      // Konvertiere Elemente zum Backend-Format, filtere Audio-Elemente heraus
      const templateElements = elements
        .filter((el) => el.type !== 'audio') // Audio-Elemente werden nicht gerendert
        .map((el) => ({
          id: el.id,
          type: el.type,
          position: el.position,
          size: el.size,
          style: el.style as Record<string, unknown>,
          content: el.content,
        }));

      // Backend erwartet: canvas, elements, variables (nicht custom_canvas etc.)
      const result = await generatePreview({
        canvas,
        elements: templateElements as never,
        variables: {},
      });

      setPreviewUrl(result);
    } catch (err) {
      console.error('Preview generation failed:', err);
    }
  };

  // Video-Export
  const handleVideoExport = async () => {
    if (!projectId) return;

    try {
      // Konvertiere Elemente zum Backend-Format (ohne Audio)
      const templateElements = elements
        .filter((el) => el.type !== 'audio')
        .map((el) => ({
          id: el.id,
          type: el.type,
          position: el.position,
          size: el.size,
          style: el.style as Record<string, unknown>,
          content: el.content,
        }));

      // Extrahiere Audio-Tracks mit Fade-Einstellungen
      // WICHTIG: fadeInDuration und fadeOutDuration beziehen sich auf die VIDEO-Dauer,
      // nicht auf die Länge der Audio-Datei!
      const audioTracks = elements
        .filter((el) => el.type === 'audio' && el.content)
        .map((el) => ({
          url: el.content!,
          volume: (el.style.volume ?? 100) / 100, // Convert 0-100 to 0-1
          fadeInDuration: el.style.fadeInDuration || 0,  // Fade-in in Sekunden
          fadeOutDuration: el.style.fadeOutDuration || 0, // Fade-out in Sekunden
          loop: el.style.audioLoop || false,
          startTime: el.style.audioStartTime,
        }));

      const request: VideoExportRequest = {
        project_id: projectId,
        canvas,
        elements: templateElements as TemplateElement[],
        audio_tracks: audioTracks.length > 0 ? audioTracks : undefined,
        duration: videoExportDuration,
        fps: 30,
      };

      const result = await exportVideo(request);

      // Video wurde erfolgreich erstellt - Media-Bibliothek aktualisieren
      await fetchMedia(projectId);

      // Video herunterladen
      if (result?.url) {
        const link = document.createElement('a');
        link.href = getMediaUrl(result.url);
        link.download = result.filename || `video_${Date.now()}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setShowVideoExportModal(false);
    } catch (err) {
      console.error('Video export failed:', err);
    }
  };

  // Element auswählen
  const handleSelectElement = useCallback((id: string | null) => {
    setSelectedElementId(id);
    setRightPanel(id ? 'element' : 'background');
  }, []);

  // Elemente zum Backend-Format konvertieren
  const getTemplateElements = (): TemplateElement[] => {
    return elements.map((el) => ({
      id: el.id,
      type: el.type === 'audio' ? 'icon' : el.type as 'text' | 'image' | 'shape' | 'icon',
      position: el.position,
      size: el.size,
      style: el.style as Record<string, unknown>,
      content: el.content,
    }));
  };

  // Berechne Textlängen-Empfehlungen basierend auf Element-Größe
  // WICHTIG: Realistische Werte für LLM/Batch-Verarbeitung - NIEMALS null für max_chars!
  const calculateTextConstraints = (el: TemplateElement, canvasWidth: number, canvasHeight: number) => {
    const style = el.style as Record<string, unknown>;
    const fontSize = (style?.fontSize as number) || 24;
    const content = el.content || '';

    // WICHTIG: Nutze die tatsächlichen Elementmaße
    // Bei Text-Elementen kann size.width/height klein oder 0 sein wenn autoSize aktiv ist
    let width = el.size.width;
    let height = el.size.height;

    // Fallback für Breite: Mindestens 50% des Canvas oder basierend auf Content
    const minReasonableWidth = fontSize * 5; // Mindestens 5 Zeichen breit
    if (!width || width < minReasonableWidth) {
      // Schätze aus Content: längste Zeile
      const lines = content.split('\n');
      const longestLine = Math.max(...lines.map(l => l.length), 10);
      width = Math.max(longestLine * fontSize * 0.6, canvasWidth * 0.5);
    }

    // Fallback für Höhe: basierend auf fontSize und Content
    const minReasonableHeight = fontSize * 1.5;
    if (!height || height < minReasonableHeight) {
      const lineCount = Math.max(1, (content.match(/\n/g) || []).length + 1);
      height = Math.max(lineCount * fontSize * 1.4, fontSize * 2);
    }

    // Durchschnittliche Zeichenbreite: ~0.5-0.6 der Schriftgröße (für proportionale Schriften)
    const avgCharWidth = fontSize * 0.55;
    // Zeichen pro Zeile (mit etwas Puffer für Varianz)
    const charsPerLine = Math.max(5, Math.floor(width / avgCharWidth * 0.9));

    // Zeilenhöhe: ~1.2-1.5 der Schriftgröße (inkl. line-height)
    const lineHeight = fontSize * 1.4;
    // Maximale Zeilen (mit Puffer für Padding)
    const maxLines = Math.max(1, Math.floor((height * 0.85) / lineHeight));

    // Empfohlene Gesamtlänge - NIEMALS null, immer einen Richtwert!
    const recommendedMaxChars = Math.max(10, charsPerLine * maxLines);

    // WICHTIG: supports_linebreaks berücksichtigt auch ob aktueller content \n enthält
    const hasExistingLinebreaks = content.includes('\n');
    // Erlaube Linebreaks wenn max_lines > 1 ODER wenn der aktuelle Content bereits welche hat
    const supportsLinebreaks = maxLines > 1 || hasExistingLinebreaks;

    // Box-Position relativ zum Canvas (Mindestens 1% um 0 zu vermeiden)
    const boxWidthPct = Math.max(1, Math.round((width / canvasWidth) * 100));
    const boxHeightPct = Math.max(1, Math.round((height / canvasHeight) * 100));

    return {
      chars_per_line: charsPerLine, // Immer ein Wert, nie null
      max_lines: supportsLinebreaks ? Math.max(maxLines, 2) : maxLines,
      recommended_max_chars: recommendedMaxChars, // Immer ein Wert, nie null
      supports_linebreaks: supportsLinebreaks,
      font_size_px: fontSize,
      // Box-Metriken als Prozent vom Canvas (Mindestens 1%)
      box_width_pct: boxWidthPct,
      box_height_pct: boxHeightPct,
    };
  };

  // Bestimme semantic_hint basierend auf Position, Größe, Content UND Element-Index
  // Der Index hilft bei der Differenzierung wenn mehrere Elemente ähnliche Eigenschaften haben
  const determineSemanticHint = (
    el: TemplateElement,
    canvasWidth: number,
    canvasHeight: number,
    elementIndex?: number,
    totalTextElements?: number
  ): 'headline' | 'cta' | 'badge' | 'main_text' => {
    const style = el.style as Record<string, unknown>;
    const fontSize = (style?.fontSize as number) || 24;
    const content = (el.content || '').toLowerCase();

    // Position im Canvas (Zentrum des Elements)
    const centerY = el.position.y + el.size.height / 2;
    const isTopArea = centerY < canvasHeight * 0.35;
    const isBottomArea = centerY > canvasHeight * 0.65;
    const isMiddleArea = !isTopArea && !isBottomArea;

    // Größenverhältnisse
    const widthRatio = el.size.width / canvasWidth;
    const isWideElement = widthRatio > 0.5;

    // Content-Analyse
    const isShortText = content.length < 25 && !content.includes('\n');
    const isVeryShortText = content.length < 15;
    const hasMultipleLines = content.includes('\n');

    // CTA-Keywords (deutsch + englisch)
    const ctaKeywords = ['jetzt', 'hier', 'mehr', 'klick', 'download', 'kaufen', 'starten', 'gratis', 'kostenlos', 'anmelden', 'now', 'click', 'buy', 'start', 'free'];
    const hasCTAKeyword = ctaKeywords.some(kw => content.includes(kw));

    // Badge-Keywords (Labels, Kategorien)
    const badgeKeywords = ['neu', 'new', 'top', 'sale', 'tipp', 'tip', 'info', 'guide', 'how', 'dein', 'your', 'gratis', 'free', 'pdf', 'video'];
    const hasBadgeKeyword = badgeKeywords.some(kw => content.includes(kw));

    // ========== REGEL-HIERARCHIE ==========

    // 1. EXPLIZITE CTA-Erkennung (Keywords sind starkes Signal)
    if (hasCTAKeyword && isShortText) {
      return 'cta';
    }

    // 2. POSITION + INDEX basierte Klassifizierung
    // Bei mehreren Textelementen: Index nutzen für bessere Differenzierung
    if (elementIndex !== undefined && totalTextElements !== undefined && totalTextElements >= 2) {
      const isFirstElement = elementIndex === 0;
      const isLastElement = elementIndex === totalTextElements - 1;

      // Erstes Element oben = meist Badge/Label
      if (isFirstElement && isTopArea && isVeryShortText) {
        return 'badge';
      }

      // Letztes Element unten = meist CTA
      if (isLastElement && isBottomArea) {
        return 'cta';
      }

      // Mittleres Element mit großer Schrift = Headline
      if (!isFirstElement && !isLastElement && fontSize >= 28) {
        return 'headline';
      }
    }

    // 3. BADGE: Kurz + Badge-Keywords oder oben + sehr kurz
    if ((hasBadgeKeyword && isVeryShortText) || (isTopArea && isVeryShortText && fontSize <= 24)) {
      return 'badge';
    }

    // 4. HEADLINE: Oben/Mitte + große Schrift + breit
    if ((isTopArea || isMiddleArea) && fontSize >= 32 && isWideElement) {
      return 'headline';
    }

    // 5. CTA: Unten positioniert
    if (isBottomArea && isShortText) {
      return 'cta';
    }

    // 6. MAIN_TEXT: Mehrzeilig oder langer Text
    if (hasMultipleLines || content.length > 50) {
      return 'main_text';
    }

    // 7. FALLBACK basierend auf vertikaler Position
    if (isTopArea) {
      return fontSize >= 28 ? 'headline' : 'badge';
    }
    if (isBottomArea) {
      return 'cta';
    }

    // Mitte = main_text
    return 'main_text';
  };

  // Berechne Bild-Constraints mit klarer Pixel-Semantik
  const calculateImageConstraints = (el: TemplateElement, canvasWidth: number, canvasHeight: number) => {
    // Box-Größe im Template
    const boxWidthPx = Math.round(el.size.width);
    const boxHeightPx = Math.round(el.size.height);

    // Relative Box-Größe (für Export)
    const boxWidthPct = Math.round((el.size.width / canvasWidth) * 100);
    const boxHeightPct = Math.round((el.size.height / canvasHeight) * 100);

    // WICHTIG: Mindestauflösung für Qualität
    // Regel: Mindestens 1080px auf der längeren Seite, ODER Canvas-Größe wenn Box > 50%
    const boxRatioX = el.size.width / canvasWidth;
    const boxRatioY = el.size.height / canvasHeight;
    const isLargeBox = boxRatioX > 0.5 || boxRatioY > 0.5;

    // Für große Boxen: volle Canvas-Auflösung
    // Für kleine Boxen: mindestens 1080px auf längerer Seite (für HD-Qualität)
    let recommendedMinWidth: number;
    let recommendedMinHeight: number;

    if (isLargeBox) {
      recommendedMinWidth = canvasWidth;
      recommendedMinHeight = canvasHeight;
    } else {
      // Skaliere auf mindestens 1080px, behalte Aspect Ratio
      const aspectRatio = boxWidthPx / boxHeightPx;
      if (aspectRatio >= 1) {
        // Landscape oder quadratisch
        recommendedMinWidth = Math.max(1080, boxWidthPx * 2);
        recommendedMinHeight = Math.round(recommendedMinWidth / aspectRatio);
      } else {
        // Portrait
        recommendedMinHeight = Math.max(1080, boxHeightPx * 2);
        recommendedMinWidth = Math.round(recommendedMinHeight * aspectRatio);
      }
    }

    // Aspect Ratio berechnen (vereinfacht)
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(boxWidthPx, boxHeightPx);
    const aspectRatioSimple = `${Math.round(boxWidthPx / divisor)}:${Math.round(boxHeightPx / divisor)}`;

    return {
      // Relative Position im Template (für Verständnis)
      box_width_pct: boxWidthPct,
      box_height_pct: boxHeightPct,
      // Empfohlene Mindestauflösung für HD-Qualität
      recommended_min_width_px: recommendedMinWidth,
      recommended_min_height_px: recommendedMinHeight,
      aspect_ratio: aspectRatioSimple,
      formats: ['jpg', 'jpeg', 'png', 'webp'],
      quality_note: 'Bilder sollten mindestens die empfohlene Auflösung haben. Upscaling von niedrigeren Auflösungen kann zu unscharfen Ergebnissen führen.',
    };
  };

  // Template als JSON-Datei herunterladen (für LLM-Nutzung)
  const downloadTemplateAsJson = (name: string, description: string, category: string, type: string, displayId?: string) => {
    const templateElements = getTemplateElements();

    // Variable Felder extrahieren (Text + Bilder)
    const textFields = templateElements.filter(el => el.type === 'text');
    const imageFields = templateElements.filter(el => el.type === 'image');

    // Content-Sheet Felder generieren (Single Source of Truth für Batch-Input)
    const contentSheetFields = [
      // Immer: eindeutige ID
      {
        field_name: 'content_id',
        type: 'string',
        required: true,
        description: 'Eindeutige ID für diesen Content (z.B. REEL-001)',
        example: `${category.toUpperCase()}-001`,
      },
      // Text-Felder (mit Index für bessere semantic_hint Differenzierung)
      ...textFields.map((el, idx) => {
        // Nutze die verbesserten Funktionen mit Canvas-Dimensionen UND Element-Index
        const constraints = calculateTextConstraints(el, canvas.width, canvas.height);
        const currentText = el.content || '';
        // Übergebe Index und Gesamtzahl für bessere Klassifizierung
        const semanticHint = determineSemanticHint(el, canvas.width, canvas.height, idx, textFields.length);

        return {
          field_name: `text_${idx + 1}`,
          element_id: el.id,
          type: 'text',
          required: true,
          semantic_hint: semanticHint, // headline, badge, cta, main_text
          current_value: currentText,
          constraints: {
            // null = unbegrenzt, Zahl = empfohlenes Limit
            max_chars: constraints.recommended_max_chars,
            max_lines: constraints.max_lines,
            chars_per_line: constraints.chars_per_line,
            // WICHTIG: supports_linebreaks ist NUR true wenn max_lines > 1
            supports_linebreaks: constraints.supports_linebreaks,
          },
          // Nur informativ, nicht für Batch-Verarbeitung relevant
          _style_info: {
            _read_only: true,
            font_size_px: constraints.font_size_px,
            box_width_pct: constraints.box_width_pct,
            box_height_pct: constraints.box_height_pct,
          },
          description: `${semanticHint === 'headline' ? 'Headline' : semanticHint === 'cta' ? 'Call-to-Action' : semanticHint === 'badge' ? 'Badge/Label' : 'Text'}: "${currentText.substring(0, 25)}${currentText.length > 25 ? '...' : ''}"`,
          example: currentText || (semanticHint === 'cta' ? 'Jetzt kaufen!' : semanticHint === 'headline' ? 'DEIN Titel hier' : 'Text hier'),
        };
      }),
      // Bild-Felder
      ...imageFields.map((el, idx) => {
        const imgConstraints = calculateImageConstraints(el, canvas.width, canvas.height);

        return {
          field_name: `image_${idx + 1}`,
          element_id: el.id,
          type: 'image',
          required: true,
          current_value: el.content || null,
          constraints: {
            recommended_min_width_px: imgConstraints.recommended_min_width_px,
            recommended_min_height_px: imgConstraints.recommended_min_height_px,
            aspect_ratio: imgConstraints.aspect_ratio,
            formats: imgConstraints.formats,
          },
          // Nur informativ (Template-relative Werte)
          _box_info: {
            _read_only: true,
            box_width_pct: imgConstraints.box_width_pct,
            box_height_pct: imgConstraints.box_height_pct,
          },
          description: `Bildelement ${idx + 1} (${imgConstraints.aspect_ratio})`,
          // Präferenz-Reihenfolge für Input
          accepts: {
            preferred: ['file_path', 'url'],
            fallback: ['image_prompt'],
            note: 'Nutze file_path/url wenn Asset vorhanden, image_prompt nur als Fallback für Generierung',
          },
          example: '/uploads/media/example.jpg',
        };
      }),
    ];

    // Plattform-Kompatibilität basierend auf Canvas-Größe ermitteln
    const matchingPreset = CANVAS_PRESETS.find(
      p => p.width === canvas.width && p.height === canvas.height
    );
    const platformCompat = matchingPreset
      ? {
          platforms: matchingPreset.platforms,
          post_types: matchingPreset.postTypes,
          format_name: matchingPreset.name,
        }
      : {
          platforms: ['facebook', 'instagram'] as const,
          post_types: ['feed'] as const,
          format_name: 'Benutzerdefiniert',
        };

    // LLM-freundliches Format mit Anweisungen
    const llmTemplate = {
      _meta: {
        format: 'Marketing Hub LLM Template',
        version: '2.5', // Upgrade: Plattform-Kompatibilität hinzugefügt
        exported_at: new Date().toISOString(),
        display_id: displayId || `LOCAL-${Date.now()}`,
      },
      template: {
        name,
        description: description || undefined,
        category,
        template_type: type,
      },
      // NEU: Plattform-Kompatibilität
      platform_compatibility: {
        platforms: platformCompat.platforms,
        post_types: platformCompat.post_types,
        format_name: platformCompat.format_name,
        _note: (platformCompat.platforms?.length ?? 0) === 2
          ? 'Dieses Format funktioniert für Facebook UND Instagram'
          : `Dieses Format ist optimiert für ${platformCompat.platforms?.[0] === 'facebook' ? 'Facebook' : 'Instagram'}`,
      },
      canvas: {
        width: canvas.width,
        height: canvas.height,
        background_type: canvas.background_type,
        background_value: canvas.background_value,
        aspect_ratio: `${canvas.width}:${canvas.height}`,
      },
      // NEU: Content-Sheet Definition für Batch-Erstellung
      content_sheet: {
        description: 'Felder die pro Content-Einheit (Post/Reel/Story) ausgefüllt werden müssen',
        fields: contentSheetFields,
        csv_header: contentSheetFields.map(f => f.field_name).join(','),
        example_row: contentSheetFields.map(f =>
          typeof f.example === 'string' ? `"${f.example.replace(/"/g, '""')}"` : `"${f.example}"`
        ).join(','),
      },
      // Vollständige Element-Definitionen (für Rendering)
      elements: templateElements.map(el => {
        const style = el.style as Record<string, unknown>;
        const isText = el.type === 'text';
        const isImage = el.type === 'image';

        return {
          ...el,
          _variable: isText || isImage,
          _constraints: isText ? calculateTextConstraints(el, canvas.width, canvas.height) : undefined,
          _description: isText
            ? `Textelement: "${el.content || 'Platzhalter'}"`
            : isImage
            ? 'Bildelement (URL oder Platzhalter)'
            : el.type === 'shape'
            ? `Form: ${style?.shapeType || 'Rechteck'}`
            : `Element vom Typ ${el.type}`,
        };
      }),
      // LLM-Anweisungen (Single Source of Truth: content_sheet.fields)
      llm_instructions: {
        purpose: 'Dieses Template definiert das visuelle Layout für Social Media Posts. Nur die Inhalte in content_sheet.fields sind variabel.',
        single_source_of_truth: 'content_sheet.fields',
        batch_workflow: [
          '1. Lies content_sheet.fields - das sind die einzigen Felder die du befüllen musst',
          '2. Beachte constraints: null = unbegrenzt, Zahl = empfohlenes Maximum',
          '3. Nutze semantic_hint für kontextgerechte Texte (headline, cta, badge, main_text)',
          '4. Für Bilder: bevorzuge file_path/url, nutze image_prompt nur als Fallback',
          '5. Erstelle pro Post/Reel ein JSON-Objekt mit allen required Feldern',
        ],
        constraint_semantics: {
          null_value: 'null bedeutet unbegrenzt (keine Begrenzung)',
          number_value: 'Zahl ist empfohlenes Maximum (weiche Grenze)',
          read_only_fields: 'Felder mit _read_only: true sind nur informativ, nicht für Input relevant',
        },
        content_guidelines: {
          text_rules: [
            'Beachte max_chars und max_lines wenn gesetzt (null = unbegrenzt)',
            'Nutze \\n für Zeilenumbrüche nur wenn supports_linebreaks=true',
            'Passe Ton an semantic_hint an: headline=aufmerksamkeitsstark, cta=handlungsorientiert, badge=kurz&prägnant',
            'Konsistenter Stil über die gesamte Serie',
          ],
          image_rules: [
            'Bevorzuge existierende Assets (file_path/url) über Generierungs-Prompts',
            'Mindestauflösung: recommended_min_width_px x recommended_min_height_px',
            'Einheitliche Bildsprache pro Serie (Motiv, Stil, Licht)',
          ],
        },
        output_format: {
          description: 'Generiere Content als JSON-Array mit einem Objekt pro Post/Reel',
          json_array: true,
          example: {
            content_id: `${category.toUpperCase()}-001`,
            ...Object.fromEntries(
              textFields.map((el, idx) => [`text_${idx + 1}`, el.content || `Text ${idx + 1}`])
            ),
            ...Object.fromEntries(
              imageFields.map((_, idx) => [`image_${idx + 1}`, `/uploads/media/image_${idx + 1}.jpg`])
            ),
          },
        },
      },
      // Optionale Erweiterungen für Serien/A-B-Tests
      series_options: {
        _note: 'Diese Felder sind optional und können für A/B-Tests oder Serien-Tracking ergänzt werden',
        optional_fields: [
          { field: 'hook_variant', type: 'string', description: 'A/B-Test Label für verschiedene Hooks (z.B. "A", "B", "emotional", "rational")' },
          { field: 'cta_variant', type: 'string', description: 'A/B-Test Label für verschiedene CTAs' },
          { field: 'topic', type: 'string', description: 'Thema/Angle für Serien-Konsistenz' },
          { field: 'publish_date', type: 'date', description: 'Geplantes Veröffentlichungsdatum (YYYY-MM-DD)' },
        ],
        consistency_tips: [
          'Definiere 3-5 wiederkehrende Motivtypen für Bilder',
          'Nutze einheitliche CTAs über die Serie',
          'Variiere Hooks/Headlines, aber behalte Format bei',
        ],
      },
    };

    // Als JSON-Datei herunterladen
    const blob = new Blob([JSON.stringify(llmTemplate, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name.replace(/[^a-zA-Z0-9-_]/g, '_')}_template.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Direktes Speichern (für bereits geladene Designs)
  const handleQuickSave = async () => {
    if (!projectId || !editingDesignId) return;

    try {
      const templateElements = getTemplateElements();
      const tagsArray = designTags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      // Thumbnail aktualisieren (Vorschau als Bild)
      let thumbnailUrl: string | undefined;
      try {
        // Filtere Audio-Elemente heraus (type cast nötig da TemplateElement keinen 'audio' Typ hat)
        const visualElements = templateElements.filter(el => (el.type as string) !== 'audio');
        thumbnailUrl = await generatePreview({
          canvas,
          elements: visualElements as never,
          variables: {},
        });
      } catch (thumbnailErr) {
        console.warn('Thumbnail update failed:', thumbnailErr);
      }

      await updateDesign(editingDesignId, {
        name: designName,
        description: designDescription || undefined,
        thumbnail_url: thumbnailUrl,
        canvas,
        elements: templateElements,
        category: designCategory,
        platform: designPlatform,
        tags: tagsArray,
      });

      // Dirty State zurücksetzen
      setIsDirty(false);
      lastSavedStateRef.current = JSON.stringify({ canvas, elements });
    } catch (err) {
      console.error('Quick save failed:', err);
    }
  };

  // Design speichern (via Modal - für neues Design oder "Speichern als...")
  const handleSaveDesign = async () => {
    if (!projectId || !designName.trim()) return;

    // Validate post data if creating post (text is optional since design provides the media)
    if (createPostAfterSave && !postDate) {
      return;
    }

    try {
      const templateElements = getTemplateElements();
      const tagsArray = designTags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      // Thumbnail generieren (Vorschau als Bild)
      let thumbnailUrl: string | undefined;
      try {
        // Filtere Audio-Elemente heraus für die Vorschau (type cast nötig)
        const visualElements = templateElements.filter(el => (el.type as string) !== 'audio');
        thumbnailUrl = await generatePreview({
          canvas,
          elements: visualElements as never,
          variables: {},
        });
      } catch (thumbnailErr) {
        console.warn('Thumbnail generation failed, saving without thumbnail:', thumbnailErr);
      }

      // Neues Design speichern (auch bei "Speichern als...")
      const newDesign = await saveDesign({
        project_id: projectId,
        name: designName,
        description: designDescription || undefined,
        thumbnail_url: thumbnailUrl,
        canvas,
        elements: templateElements,
        category: designCategory,
        platform: designPlatform,
        tags: tagsArray,
        flight_id: designFlightId || undefined,
      });

      // Jetzt das neue Design als "aktiv" setzen
      setEditingDesignId(newDesign.id);

      // Dirty State zurücksetzen
      setIsDirty(false);
      lastSavedStateRef.current = JSON.stringify({ canvas, elements });

      // Create post if option is enabled
      if (createPostAfterSave && thumbnailUrl && postText.trim() && postDate) {
        setIsCreatingPost(true);
        try {
          // Determine platforms based on design platform
          const platforms = designPlatform === 'both'
            ? ['instagram', 'facebook']
            : [designPlatform];

          await createPost({
            project_id: projectId,
            text: postText.trim(),
            media_urls: [thumbnailUrl],
            platforms,
            scheduled_for: new Date(postDate).toISOString(),
            status: 'scheduled',
            flight_id: designFlightId,
          });
        } catch (postErr) {
          console.error('Failed to create post:', postErr);
        } finally {
          setIsCreatingPost(false);
        }
      }

      // Reset post form
      setCreatePostAfterSave(false);
      setPostText('');
      setPostDate('');

      // Modal schließen
      setShowSaveModal(false);
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  // "Speichern als..." - öffnet Modal mit leerem Namen für neue Variante
  const handleSaveAs = () => {
    // Name anpassen für Variante
    if (designName) {
      setDesignName(`${designName} (Kopie)`);
    }
    setShowSaveModal(true);
  };

  // Klick auf Speichern-Button im Header
  const handleSaveClick = () => {
    if (editingDesignId) {
      // Bereits geladenes Design direkt aktualisieren
      handleQuickSave();
    } else {
      // Neues Design - Modal öffnen
      setShowSaveModal(true);
    }
  };

  // Formular zurücksetzen
  const resetSaveForm = () => {
    setDesignName('');
    setDesignDescription('');
    setDesignCategory('post');
    setDesignPlatform('both');
    setDesignTags('');
    setDesignFlightId(null);
    setEditingDesignId(null);
  };

  // Gespeichertes Design laden
  const handleLoadDesign = (design: SavedDesign) => {
    // Elemente konvertieren
    const editorElements: CanvasElement[] = [
      ...design.elements.map((el) => ({
        id: el.id,
        type: el.type === 'icon' ? 'audio' as const : el.type as 'text' | 'image' | 'shape',
        position: el.position,
        size: el.size,
        style: el.style,
        content: el.content || '',
      })),
      ...(design.audio_tracks || []).map((el) => ({
        id: el.id,
        type: 'audio' as const,
        position: el.position,
        size: el.size,
        style: el.style,
        content: el.content || '',
      })),
    ];

    // WICHTIG: lastSavedStateRef ZUERST setzen, BEVOR State-Updates!
    // Sonst triggert der dirty-tracking useEffect bevor wir isDirty=false setzen
    lastSavedStateRef.current = JSON.stringify({ canvas: design.canvas, elements: editorElements });

    // Dirty State zurücksetzen BEVOR andere State-Updates
    setIsDirty(false);

    // Canvas und Elemente setzen
    setCanvas(design.canvas);
    setElements(editorElements);

    // Tracking setzen für Update - die neue Design-ID
    setEditingDesignId(design.id);

    // Speichern-Modal vorausfüllen für schnelles Update
    setDesignName(design.name);
    setDesignDescription(design.description || '');
    setDesignCategory(design.category);
    setDesignPlatform(design.platform);
    setDesignTags(design.tags?.join(', ') || '');
    setDesignFlightId(design.flight_id || null);

    // Panel schließen
    setShowSavedDesigns(false);
    setSelectedElementId(null);
    setPreviewUrl(null);

    loadDesign(design);
  };

  // Design löschen
  const handleDeleteDesign = async (designId: string) => {
    if (!confirm(t('creator:deleteConfirm'))) return;
    try {
      await deleteDesign(designId);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // Cycle design status: draft → in_progress → completed → draft
  const handleCycleStatus = async (design: SavedDesign, e: React.MouseEvent) => {
    e.stopPropagation();
    const statusOrder: DesignStatus[] = ['draft', 'in_progress', 'completed'];
    const currentIndex = statusOrder.indexOf(design.status || 'draft');
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    const nextStatus = statusOrder[nextIndex];

    try {
      await updateDesign(design.id, { status: nextStatus });
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  // Open "Create Post from Design" modal
  const handleOpenCreatePostModal = (design: SavedDesign) => {
    setSelectedDesignForPost(design);
    setCreatePostText('');
    setCreatePostDate('');
    setCreatePostFlightId(design.flight_id || null);
    setShowCreatePostModal(true);
  };

  // Create post from saved design
  const handleCreatePostFromDesign = async () => {
    if (!selectedDesignForPost || !projectId || !createPostDate) return;

    setIsCreatingPost(true);
    try {
      // Determine platforms based on design platform
      const platforms = selectedDesignForPost.platform === 'both'
        ? ['instagram', 'facebook']
        : [selectedDesignForPost.platform];

      // Use the design's thumbnail as media
      const mediaUrl = selectedDesignForPost.thumbnail_url
        ? getMediaUrl(selectedDesignForPost.thumbnail_url)
        : null;

      await createPost({
        project_id: projectId,
        text: createPostText.trim() || '', // Text is optional
        media_urls: mediaUrl ? [mediaUrl] : [],
        platforms,
        scheduled_for: new Date(createPostDate).toISOString(),
        status: 'scheduled',
        flight_id: createPostFlightId || undefined,
      });

      // Reset and close
      setShowCreatePostModal(false);
      setSelectedDesignForPost(null);
      setCreatePostText('');
      setCreatePostDate('');
      setCreatePostFlightId(null);
    } catch (err) {
      console.error('Failed to create post from design:', err);
    } finally {
      setIsCreatingPost(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 px-6 py-2 flex-shrink-0">
        <Breadcrumb items={[
          { label: t('creator:projects'), href: '/projects' },
          ...(selectedProject ? [{ label: selectedProject.name, href: `/projects/${selectedProject.id}` }] : []),
          { label: t('creator:title') },
        ]} />
      </div>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <div className="flex items-center gap-2">
                <CreatorIcon className="w-6 h-6 text-gray-400" />
                <h1 className="text-xl font-semibold text-gray-900">Creator Studio</h1>
                {editingDesignId && designName && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                    {designName}
                  </span>
                )}
                {editingLLMTemplateId && templateName && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded" title={t('creator:templateEditing')}>
                    {t('creator:template')}: {templateName}
                  </span>
                )}
                {isDirty && autoSaveStatus === 'idle' && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded" title={t('creator:unsavedChanges')}>
                    {t('creator:notSaved')}
                  </span>
                )}
                {autoSaveStatus === 'saving' && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded flex items-center gap-1" title={t('creator:saving')}>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('creator:saving')}
                  </span>
                )}
                {autoSaveStatus === 'saved' && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded flex items-center gap-1" title={lastAutoSave ? `${t('creator:lastSaved')}: ${lastAutoSave.toLocaleTimeString()}` : t('creator:saved')}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('creator:saved')}
                  </span>
                )}
                {autoSaveStatus === 'error' && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded flex items-center gap-1" title={t('creator:autoSaveFailed')}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('creator:error')}
                  </span>
                )}
              </div>
              {/* Projekt-Auswahl wenn nicht via URL - durchsuchbar */}
              {!urlProjectId && projects.length > 0 && (
                <div className="relative mt-1" ref={projectDropdownRef}>
                  <button
                    onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 cursor-pointer"
                  >
                    {selectedProject?.name || t('creator:selectProject')}
                    <span className="text-xs text-gray-400">({selectedProject?.display_id})</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showProjectDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="p-2 border-b">
                        <input
                          type="text"
                          value={projectSearch}
                          onChange={(e) => setProjectSearch(e.target.value)}
                          placeholder={t('creator:searchProject')}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {filteredProjects.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            {t('creator:noProjectsFound')}
                          </div>
                        ) : (
                          filteredProjects.map((p) => (
                            <button
                              key={p.id}
                              onClick={() => {
                                handleProjectChange(p.id);
                                setShowProjectDropdown(false);
                                setProjectSearch('');
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center justify-between ${
                                projectId === p.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                              }`}
                            >
                              <span className="truncate">{p.name}</span>
                              <span className="text-xs text-gray-400 ml-2">{p.display_id}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Statische Projekt-Anzeige wenn via URL */}
              {urlProjectId && selectedProject && (
                <p className="text-sm text-gray-500">{selectedProject.name}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* ═══ GRUPPE 1: Neu & Öffnen ═══ */}
            <div className="flex items-center">
              {/* Neu-Button */}
              <button
                onClick={handleNewDesign}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-l-lg border border-gray-300 border-r-0 transition-all flex items-center gap-1.5"
                title={t('creator:newEmptyDesign')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('creator:new')}
              </button>

              {/* Öffnen-Dropdown-Gruppe */}
              <button
                onClick={() => setShowSavedDesigns(true)}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-300 border-r-0 transition-all flex items-center gap-1.5"
                title={t('creator:openSavedDesigns')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                </svg>
                {t('creator:designs')}
                {savedDesigns.length > 0 && (
                  <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">{savedDesigns.length}</span>
                )}
              </button>

              <button
                onClick={() => setShowLLMTemplates(true)}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-r-lg border border-gray-300 transition-all flex items-center gap-1.5"
                title={t('creator:openLlmTemplates')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                </svg>
                {t('creator:templates')}
                {llmTemplates.length > 0 && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{llmTemplates.length}</span>
                )}
              </button>
            </div>

            {/* Trenner */}
            <div className="h-6 w-px bg-gray-200" />

            {/* ═══ GRUPPE 2: AI-Tools ═══ */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowAIBriefing(true)}
                className="px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 rounded-lg
                         hover:from-violet-700 hover:to-indigo-700 flex items-center gap-1.5 shadow-sm transition-all"
                title={t('creator:aiContentGeneration')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {t('creator:aiGenerator')}
              </button>
              {projectId && (
                <button
                  onClick={() => navigate(`/projects/${projectId}/ai-review`)}
                  className="px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50 rounded-lg flex items-center gap-1.5 border border-violet-200 transition-all"
                  title={t('creator:reviewGeneratedContent')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('creator:review')}
                </button>
              )}
            </div>

            {/* Trenner */}
            <div className="h-6 w-px bg-gray-200" />

            {/* ═══ GRUPPE 3: Vorschau & Export ═══ */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleGeneratePreview}
                disabled={isGenerating}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
                title={t('creator:renderPreview')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {isGenerating ? t('creator:rendering') : t('creator:preview')}
              </button>

              {/* Download-Button erscheint nach Vorschau */}
              {previewUrl && (
                <a
                  href={previewUrl}
                  download={`design-${Date.now()}.png`}
                  className="px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 rounded-lg border border-green-300 transition-all flex items-center gap-1.5"
                  title={t('creator:downloadPng')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  PNG
                </a>
              )}

              {/* Video-Export nur bei Audio */}
              {hasAudioElements && (
                <button
                  onClick={() => setShowVideoExportModal(true)}
                  disabled={isExportingVideo || !ffmpegAvailable}
                  className="px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg border border-purple-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
                  title={!ffmpegAvailable ? t('creator:ffmpegRequired') : t('creator:exportVideo')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {isExportingVideo ? t('creator:exporting') : 'MP4'}
                </button>
              )}
            </div>

            {/* Trenner */}
            <div className="h-6 w-px bg-gray-200" />

            {/* ═══ GRUPPE 4: Speichern (Primäre Aktionen) ═══ */}
            <div className="flex items-center gap-2">
              {/* Sekundäre Speicher-Optionen */}
              {editingDesignId && (
                <button
                  onClick={handleSaveAs}
                  className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                  title={t('creator:createCopy')}
                >
                  {t('creator:copy')}
                </button>
              )}

              {/* Template speichern */}
              <button
                onClick={() => setShowTemplateModal(true)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                  editingLLMTemplateId
                    ? 'text-white bg-purple-600 hover:bg-purple-700'
                    : 'text-purple-700 hover:bg-purple-50 border border-purple-300'
                }`}
                title={editingLLMTemplateId ? t('creator:updateTemplate') : t('creator:saveAsTemplate')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                </svg>
                {t('creator:template')}
              </button>

              {/* Primärer Speichern-Button */}
              <button
                onClick={handleSaveClick}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg
                         hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition-all"
              >
                {isSaving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('creator:saving')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('creator:save')}
                  </>
                )}
              </button>

              {/* Post aus Design erstellen Button - nur bei geladenem Design */}
              {editingDesignId && (
                <button
                  onClick={() => {
                    const design = savedDesigns.find(d => d.id === editingDesignId);
                    if (design) handleOpenCreatePostModal(design);
                  }}
                  className="px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 rounded-lg border border-green-300 transition-all flex items-center gap-1.5"
                  title={t('creator:createPostFromDesign')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t('creator:createPost')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Templates */}
        <TemplateSidebar
          templates={templates}
          selectedTemplate={selectedTemplate}
          isLoading={isLoading}
          categoryFilter={categoryFilter}
          onCategoryChange={setCategoryFilter}
          onSelectTemplate={selectTemplate}
          onNewDesign={handleNewDesign}
        />

        {/* Center - Canvas Editor */}
        <div className="flex-1 flex flex-col overflow-hidden p-4">
          <CanvasEditor
            key={`canvas-${fontsReady}`}
            canvas={canvas}
            elements={elements}
            selectedElementId={selectedElementId}
            selectedElementIds={selectedElementIds}
            onCanvasChange={setCanvas}
            onElementsChange={setElements}
            onSelectElement={handleSelectElement}
            onSelectMultiple={setSelectedElementIds}
            onAddElement={handleAddElement}
            onOpenElementLibrary={() => setShowElementLibrary(true)}
            onGroupElements={handleGroupElements}
            onUngroupElements={handleUngroupElements}
          />
        </div>

        {/* Right Sidebar - Properties */}
        <div className="w-80 bg-white border-l border-gray-200 overflow-hidden flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setRightPanel('background')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                rightPanel === 'background'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('creator:background')}
            </button>
            <button
              onClick={() => setRightPanel('element')}
              disabled={!selectedElement}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                rightPanel === 'element' && selectedElement
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 disabled:text-gray-300'
              }`}
            >
              {t('creator:element')}
            </button>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto">
            {rightPanel === 'background' && (
              <BackgroundEditor canvas={canvas} onChange={setCanvas} />
            )}

            {rightPanel === 'element' && selectedElement && (
              <ElementProperties
                element={selectedElement}
                canvas={canvas}
                onChange={handleElementChange}
                onDelete={handleDeleteElement}
                onDuplicate={handleDuplicateElement}
                onMoveUp={() => handleMoveElement('up')}
                onMoveDown={() => handleMoveElement('down')}
                onOpenMediaPicker={
                  ['image', 'shape', 'audio'].includes(selectedElement.type)
                    ? () => setShowMediaPicker(true)
                    : undefined
                }
              />
            )}

            {rightPanel === 'element' && !selectedElement && (
              <div className="p-8 text-center text-gray-400">
                <p>{t('creator:selectOrAddElement')}</p>
              </div>
            )}
          </div>

          {/* Preview Section - Klickbar für größere Ansicht */}
          {previewUrl && (
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">{t('creator:preview')}</h3>
                <button
                  onClick={() => setPreviewUrl(null)}
                  className="text-gray-400 hover:text-gray-600"
                  title={t('creator:closePreview')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <button
                onClick={() => setShowPreviewModal(true)}
                className="w-full cursor-zoom-in hover:opacity-90 transition-opacity"
                title="Klicken für Vollbildansicht"
              >
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full rounded-lg shadow"
                />
              </button>
              <p className="text-xs text-gray-400 mt-1 text-center">{t('creator:clickForFullscreen')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Save Modal - für neues Design oder "Speichern als..." */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingDesignId ? t('creator:saveNewVariant') : t('creator:saveDesign')}
            </h3>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('creator:designName')} *
                </label>
                <input
                  type="text"
                  value={designName}
                  onChange={(e) => setDesignName(e.target.value)}
                  placeholder={t('creator:designNamePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('creator:description')}
                </label>
                <textarea
                  value={designDescription}
                  onChange={(e) => setDesignDescription(e.target.value)}
                  placeholder={t('creator:descriptionPlaceholder')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Category & Platform */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('creator:category')}
                  </label>
                  <select
                    value={designCategory}
                    onChange={(e) => setDesignCategory(e.target.value as typeof designCategory)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="post">{t('creator:formats.post')}</option>
                    <option value="story">{t('creator:formats.story')}</option>
                    <option value="reel">{t('creator:formats.reel')}</option>
                    <option value="carousel">{t('creator:formats.carousel')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('creator:platform')}
                  </label>
                  <select
                    value={designPlatform}
                    onChange={(e) => setDesignPlatform(e.target.value as typeof designPlatform)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="both">{t('creator:both')}</option>
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('creator:tags')}
                </label>
                <input
                  type="text"
                  value={designTags}
                  onChange={(e) => setDesignTags(e.target.value)}
                  placeholder={t('creator:tagsPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Flight (optional) */}
              {flights.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('creator:flight')}
                  </label>
                  <select
                    value={designFlightId || ''}
                    onChange={(e) => setDesignFlightId(e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t('creator:noFlight')}</option>
                    {flights.map((flight) => (
                      <option key={flight.id} value={flight.id}>
                        {flight.name} ({flight.display_id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Create Post Option */}
              <div className="border-t pt-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createPostAfterSave}
                    onChange={(e) => setCreatePostAfterSave(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">{t('creator:alsoSchedulePost')}</span>
                </label>

                {createPostAfterSave && (
                  <div className="mt-3 pl-6 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('creator:postText')} <span className="text-gray-400 font-normal">({t('creator:postTextOptional')})</span>
                      </label>
                      <textarea
                        value={postText}
                        onChange={(e) => setPostText(e.target.value)}
                        placeholder={t('creator:captionOrEmpty')}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('creator:dateTime')} *
                      </label>
                      <input
                        type="datetime-local"
                        value={postDate}
                        onChange={(e) => setPostDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('creator:platformInfo')}: {designPlatform === 'both' ? 'Instagram & Facebook' : designPlatform === 'instagram' ? 'Instagram' : 'Facebook'}
                      {designFlightId && ` | ${t('creator:flightWillBeApplied')}`}
                    </p>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p>{elements.length} {elements.length !== 1 ? t('creator:elementsCountPlural') : t('creator:elementsCount')}</p>
                <p>{t('creator:format')}: {canvas.width} x {canvas.height}px</p>
                {editingDesignId && (
                  <p className="text-green-600 mt-1">{t('creator:willBeSavedAsNewVariant')}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setCreatePostAfterSave(false);
                  setPostText('');
                  setPostDate('');
                  // Bei "Speichern als..." den Namen wiederherstellen
                  if (editingDesignId) {
                    const original = savedDesigns.find(d => d.id === editingDesignId);
                    if (original) setDesignName(original.name);
                  } else {
                    resetSaveForm();
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('creator:cancel')}
              </button>
              <button
                onClick={handleSaveDesign}
                disabled={!designName.trim() || isSaving || isCreatingPost || (createPostAfterSave && !postDate)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg
                         hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {(isSaving || isCreatingPost) && (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {isCreatingPost ? t('creator:postCreating') : editingDesignId ? t('creator:saveAsCopy') : createPostAfterSave ? t('creator:saveAndCreatePost') : t('creator:save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Designs Modal */}
      {showSavedDesigns && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('creator:savedDesigns')}</h3>
              <button
                onClick={() => setShowSavedDesigns(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {savedDesigns.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="text-lg font-medium">{t('creator:noDesignsSaved')}</p>
                  <p className="text-sm mt-1">{t('creator:createAndSaveDesign')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {savedDesigns.map((design) => (
                    <div
                      key={design.id}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 hover:shadow-md transition-all group"
                    >
                      {/* Preview - mit Thumbnail oder Fallback */}
                      <div
                        className="h-40 flex items-center justify-center overflow-hidden"
                        style={{
                          background: design.thumbnail_url
                            ? '#f9fafb'
                            : design.canvas.background_type === 'gradient'
                              ? design.canvas.background_value
                              : design.canvas.background_type === 'color'
                                ? design.canvas.background_value
                                : '#f3f4f6',
                        }}
                      >
                        {design.thumbnail_url ? (
                          <img
                            src={getMediaUrl(design.thumbnail_url)}
                            alt={design.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="text-center">
                            <svg className="w-8 h-8 mx-auto text-gray-300 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-gray-400 text-xs">
                              {design.canvas.width} x {design.canvas.height}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-3">
                        <div className="flex items-center gap-2">
                          {/* Status indicator */}
                          <span
                            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              design.status === 'completed' ? 'bg-green-500' :
                              design.status === 'in_progress' ? 'bg-orange-500' : 'bg-gray-300'
                            }`}
                            title={
                              design.status === 'completed' ? t('creator:statusCompleted') :
                              design.status === 'in_progress' ? t('creator:statusInProgress') : t('creator:statusDraft')
                            }
                          />
                          <h4 className="font-medium text-sm text-gray-900 truncate">{design.name}</h4>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {design.category}
                          </span>
                          <span className="text-xs text-gray-400">
                            {design.platform === 'both' ? 'FB+IG' : design.platform.toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        {design.tags && design.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {design.tags.slice(0, 2).map((tag) => (
                              <span key={tag} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">
                                {tag}
                              </span>
                            ))}
                            {design.tags.length > 2 && (
                              <span className="text-xs text-gray-400">+{design.tags.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="p-2 bg-gray-50 flex items-center gap-1">
                        <button
                          onClick={() => handleLoadDesign(design)}
                          className="flex-1 px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded"
                          title={t('creator:loadDesignInEditor')}
                        >
                          {t('creator:load')}
                        </button>
                        <button
                          onClick={() => handleOpenCreatePostModal(design)}
                          className="px-2 py-1.5 text-green-600 hover:bg-green-50 rounded"
                          title={t('creator:createPostAction')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleCycleStatus(design, e)}
                          className={`px-2 py-1.5 rounded ${
                            design.status === 'completed' ? 'text-green-600 hover:bg-green-50' :
                            design.status === 'in_progress' ? 'text-orange-600 hover:bg-orange-50' :
                            'text-gray-400 hover:bg-gray-100'
                          }`}
                          title={
                            design.status === 'completed' ? t('creator:statusCompletedToDraft') :
                            design.status === 'in_progress' ? t('creator:statusInProgressToCompleted') :
                            t('creator:statusDraftToInProgress')
                          }
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteDesign(design.id)}
                          className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded"
                          title={t('creator:delete')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setShowSavedDesigns(false)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                {t('creator:close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Picker Modal */}
      {showMediaPicker && projectId && (
        <MediaPickerModal
          projectId={projectId}
          onSelect={(media) => handleMediaSelect(media.url)}
          onClose={() => setShowMediaPicker(false)}
          filterType={selectedElement?.type === 'audio' ? 'audio' : 'image'}
        />
      )}

      {/* Element Library Modal */}
      <ElementLibraryPro
        isOpen={showElementLibrary}
        onClose={() => setShowElementLibrary(false)}
        onSelectElement={handleAddLibraryElement}
        initialColor={selectedElement?.style?.backgroundColor || '#3B82F6'}
      />

      {/* Preview Modal - Vollbildansicht */}
      {showPreviewModal && previewUrl && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setShowPreviewModal(false)}
        >
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4">
            <div className="text-white text-sm">
              <span className="font-medium">{t('creator:preview')}</span>
              <span className="text-gray-400 ml-2">{canvas.width} x {canvas.height}px</span>
              {hasAudioElements && (
                <span className="ml-2 px-2 py-0.5 bg-purple-600 rounded text-xs">{t('creator:withAudio')}</span>
              )}
            </div>
            <button
              onClick={() => setShowPreviewModal(false)}
              className="text-white hover:text-gray-300 p-2"
              title={t('creator:closeEsc')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Preview Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center justify-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewUrl}
              alt="Preview"
              className="max-w-full max-h-[70vh] rounded-lg shadow-2xl"
              style={{ objectFit: 'contain' }}
            />

            {/* Audio Player für Audio-Elemente */}
            {hasAudioElements && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 w-full max-w-md">
                <div className="flex items-center gap-2 mb-2 text-white text-sm">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  <span>{t('creator:audioTracks')} ({elements.filter(el => el.type === 'audio' && el.content).length})</span>
                </div>
                {elements
                  .filter(el => el.type === 'audio' && el.content)
                  .map((audioEl, index) => (
                    <div key={audioEl.id} className="mb-2 last:mb-0">
                      <div className="text-xs text-gray-400 mb-1">
                        {t('creator:track')} {index + 1}: {audioEl.content?.split('/').pop()}
                      </div>
                      <audio
                        controls
                        className="w-full h-8"
                        style={{ filter: 'invert(1)' }}
                      >
                        <source src={audioEl.content} />
                      </audio>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Footer - Actions */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-4 p-4">
            <a
              href={previewUrl}
              download={`design-${Date.now()}.png`}
              onClick={(e) => e.stopPropagation()}
              className="px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {t('creator:saveImage')}
            </a>
            {hasAudioElements && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPreviewModal(false);
                  setShowVideoExportModal(true);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('creator:exportAsVideo')}
              </button>
            )}
            <button
              onClick={() => setShowPreviewModal(false)}
              className="px-4 py-2 text-white border border-white/30 rounded-lg font-medium hover:bg-white/10"
            >
              {t('creator:close')}
            </button>
          </div>
        </div>
      )}

      {/* Video Export Modal */}
      {showVideoExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('creator:videoExport')}</h3>
                <p className="text-sm text-gray-500">{t('creator:designWithAudio')}</p>
              </div>
            </div>

            {!ffmpegAvailable ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="font-medium text-red-800">ffmpeg nicht installiert</p>
                    <p className="text-sm text-red-700 mt-1">
                      Für Video-Export wird ffmpeg benötigt. Installation:
                    </p>
                    <ul className="text-sm text-red-600 mt-2 space-y-1">
                      <li>macOS: <code className="bg-red-100 px-1 rounded">brew install ffmpeg</code></li>
                      <li>Ubuntu: <code className="bg-red-100 px-1 rounded">sudo apt install ffmpeg</code></li>
                      <li>Windows: <code className="bg-red-100 px-1 rounded">choco install ffmpeg</code></li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Audio-Tracks Info */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm text-purple-700">
                    <span className="font-medium">{elements.filter(el => el.type === 'audio' && el.content).length}</span> Audio-Track(s) gefunden
                  </p>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Video-Dauer (Sekunden)
                  </label>
                  <input
                    type="number"
                    value={videoExportDuration}
                    onChange={(e) => setVideoExportDuration(Math.min(180, Math.max(1, parseInt(e.target.value) || 10)))}
                    min={1}
                    max={180}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max. 180 Sekunden (3 Minuten)</p>
                </div>

                {/* Format Info */}
                <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg space-y-1">
                  <p>Format: {canvas.width} x {canvas.height}px</p>
                  <p>Ausgabe: MP4 (H.264 + AAC)</p>
                  <p>FPS: 30</p>
                </div>

                {/* Last Export Result */}
                {lastVideoExport && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-green-800 mb-2">Letzter Export erfolgreich!</p>
                    <a
                      href={getMediaUrl(lastVideoExport.url)}
                      download={lastVideoExport.filename}
                      className="text-sm text-green-700 underline hover:text-green-900 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      {lastVideoExport.filename} herunterladen
                    </a>
                    <p className="text-xs text-green-600 mt-1">
                      Dauer: {lastVideoExport.duration.toFixed(1)}s
                    </p>
                    <p className="text-xs text-green-600">
                      Gespeichert in Medienbibliothek
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowVideoExportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('creator:cancel')}
              </button>
              <button
                onClick={handleVideoExport}
                disabled={!ffmpegAvailable || isExportingVideo}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg
                         hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isExportingVideo ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('creator:exporting')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {t('creator:exportVideo')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LLM Templates Liste Modal */}
      {showLLMTemplates && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('creator:llmTemplates')}</h3>
                  <p className="text-sm text-gray-500">{t('creator:llmTemplatesDescription')}</p>
                </div>
              </div>
              <button
                onClick={() => setShowLLMTemplates(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {llmTemplates.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mx-auto text-purple-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                  <p className="text-lg font-medium">{t('creator:noTemplatesAvailable')}</p>
                  <p className="text-sm mt-1">{t('creator:createDesignAndSaveAsTemplate')}</p>
                  <button
                    onClick={() => {
                      setShowLLMTemplates(false);
                      setShowTemplateModal(true);
                    }}
                    className="mt-4 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                  >
                    {t('creator:createNewTemplate')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {llmTemplates.map((template) => (
                    <div
                      key={template.id}
                      className={`border rounded-lg overflow-hidden hover:shadow-md transition-all group ${
                        editingLLMTemplateId === template.id
                          ? 'border-purple-500 ring-2 ring-purple-200'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      {/* Thumbnail Preview */}
                      <div
                        className="h-36 relative overflow-hidden cursor-pointer"
                        style={{
                          background: template.canvas.background_type === 'gradient'
                            ? template.canvas.background_value
                            : template.canvas.background_type === 'color'
                              ? template.canvas.background_value
                              : '#f3f4f6',
                        }}
                        onClick={() => {
                          // Template in Editor laden
                          const editorElements = template.elements.map((el, idx) => ({
                            ...el,
                            id: el.id || `loaded-${idx}-${Date.now()}`,
                            type: el.type as CanvasElement['type'],
                          })) as CanvasElement[];
                          lastSavedStateRef.current = JSON.stringify({ canvas: template.canvas, elements: editorElements });
                          setIsDirty(false);
                          setCanvas(template.canvas as CanvasSettings);
                          setElements(editorElements);
                          setEditingLLMTemplateId(template.id);
                          setTemplateName(template.name);
                          setTemplateDescription(template.description || '');
                          setTemplateCategory(template.category);
                          setTemplateType(template.template_type);
                          setShowLLMTemplates(false);
                        }}
                      >
                        {/* Mini-Preview der Elemente */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div
                            className="relative"
                            style={{
                              width: '80%',
                              aspectRatio: `${template.canvas.width} / ${template.canvas.height}`,
                              maxHeight: '90%',
                            }}
                          >
                            {template.elements.slice(0, 5).map((el, idx) => (
                              <div
                                key={idx}
                                className="absolute"
                                style={{
                                  left: `${el.position.x}%`,
                                  top: `${el.position.y}%`,
                                  width: `${el.size.width}%`,
                                  height: `${el.size.height}%`,
                                  backgroundColor: el.type === 'shape'
                                    ? (el.style.backgroundColor as string) || '#3B82F6'
                                    : el.type === 'text'
                                      ? 'transparent'
                                      : '#E5E7EB',
                                  borderRadius: el.type === 'shape' && el.style.shapeType === 'circle' ? '50%' :
                                    el.style.borderRadius ? `${Math.min(el.style.borderRadius as number, 20)}px` : '4px',
                                  opacity: 0.9,
                                  overflow: 'hidden',
                                }}
                              >
                                {el.type === 'text' && (
                                  <div
                                    className="w-full h-full flex items-center justify-center text-center overflow-hidden"
                                    style={{
                                      fontSize: '6px',
                                      color: (el.style.color as string) || '#000',
                                      fontWeight: el.style.fontWeight as string || 'normal',
                                    }}
                                  >
                                    {(el.content || 'Text').substring(0, 20)}
                                  </div>
                                )}
                                {el.type === 'image' && el.content && (
                                  <img src={getMediaUrl(el.content)} alt="" className="w-full h-full object-cover" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-purple-600/0 group-hover:bg-purple-600/10 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white px-3 py-1.5 rounded-lg shadow text-sm font-medium text-purple-600">
                            Laden
                          </span>
                        </div>
                        {/* Editing Badge */}
                        {editingLLMTemplateId === template.id && (
                          <div className="absolute top-2 right-2 px-2 py-0.5 text-xs bg-purple-500 text-white rounded">
                            In Bearbeitung
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-3">
                        <h4 className="font-medium text-sm text-gray-900 truncate">{template.name}</h4>
                        {template.description && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{template.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {template.category}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded">
                            {template.template_type}
                          </span>
                        </div>
                      </div>

                      {/* Actions - Icons immer sichtbar */}
                      <div className="px-3 pb-3 flex items-center gap-1">
                        {/* Content Import Button - JSON vom LLM einfügen */}
                        <button
                          onClick={() => {
                            setShowContentImport({ templateId: template.id, templateName: template.name });
                            setContentImportJson('');
                          }}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Content-JSON importieren (vom LLM)"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            // Template als optimiertes JSON herunterladen (v2.0)
                            const textFields = template.elements.filter(el => el.type === 'text');
                            const imageFields = template.elements.filter(el => el.type === 'image');

                            // Content-Sheet Felder generieren
                            const contentSheetFields = [
                              {
                                field_name: 'content_id',
                                type: 'string',
                                required: true,
                                description: 'Eindeutige ID für diesen Content',
                                example: `${template.category.toUpperCase()}-001`,
                              },
                              ...textFields.map((el, idx) => {
                                const style = el.style as Record<string, unknown>;
                                const fontSize = (style?.fontSize as number) || 24;
                                const charsPerLine = Math.floor(el.size.width / (fontSize * 0.6));
                                const maxLines = Math.floor(el.size.height / (fontSize * 1.4));
                                return {
                                  field_name: `text_${idx + 1}`,
                                  element_id: el.id,
                                  type: 'text',
                                  required: true,
                                  current_value: el.content || '',
                                  constraints: { max_chars: charsPerLine * maxLines, max_lines: maxLines, chars_per_line: charsPerLine },
                                  description: `Textelement ${idx + 1}`,
                                  example: el.content || `Text ${idx + 1}`,
                                };
                              }),
                              ...imageFields.map((el, idx) => ({
                                field_name: `image_${idx + 1}`,
                                element_id: el.id,
                                type: 'image',
                                required: true,
                                constraints: { min_width: el.size.width, min_height: el.size.height },
                                description: `Bildelement ${idx + 1}`,
                                example: '/uploads/media/example.jpg',
                              })),
                            ];

                            const llmTemplate = {
                              _meta: {
                                format: 'Marketing Hub LLM Template',
                                version: '2.0',
                                exported_at: new Date().toISOString(),
                                display_id: template.display_id,
                              },
                              template: {
                                name: template.name,
                                description: template.description || undefined,
                                category: template.category,
                                template_type: template.template_type,
                              },
                              canvas: template.canvas,
                              content_sheet: {
                                description: 'Felder die pro Content-Einheit ausgefüllt werden müssen',
                                fields: contentSheetFields,
                                csv_header: contentSheetFields.map(f => f.field_name).join(','),
                              },
                              elements: template.elements.map(el => ({
                                ...el,
                                _variable: el.type === 'text' || el.type === 'image',
                                _description: el.type === 'text'
                                  ? `Textelement: "${el.content || 'Platzhalter'}"`
                                  : el.type === 'image'
                                  ? 'Bildelement (URL oder Platzhalter)'
                                  : `Element vom Typ ${el.type}`,
                              })),
                              llm_instructions: {
                                purpose: 'Dieses Template definiert das visuelle Layout für Social Media Posts.',
                                batch_workflow: [
                                  '1. Nutze content_sheet.fields um zu verstehen welche Inhalte benötigt werden',
                                  '2. Erstelle pro Post/Reel eine Zeile mit allen Pflichtfeldern',
                                  '3. Beachte die constraints um Textüberläufe zu vermeiden',
                                ],
                                content_guidelines: {
                                  text_rules: ['Halte dich an max_chars und max_lines', 'Konsistenter Ton über die Serie'],
                                  image_rules: ['Einheitliche Bildsprache pro Serie', 'Mindestauflösung beachten'],
                                },
                              },
                            };
                            const blob = new Blob([JSON.stringify(llmTemplate, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${template.name.replace(/[^a-zA-Z0-9-_]/g, '_')}_template.json`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Als JSON herunterladen"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm(`Template "${template.name}" wirklich löschen?`)) {
                              await deleteLLMTemplate(template.id);
                            }
                          }}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title="Löschen"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-between">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowLLMTemplates(false);
                    setShowTemplateModal(true);
                  }}
                  className="px-4 py-2 text-sm font-medium text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50"
                >
                  Neues Template erstellen
                </button>
                <label className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 cursor-pointer flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  JSON importieren
                  <input
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !projectId) return;

                      try {
                        const text = await file.text();
                        const json = JSON.parse(text);

                        // Validate basic structure
                        if (!json.template || !json.canvas || !json.elements) {
                          alert('Ungültiges Template-Format. Benötigt: template, canvas, elements');
                          return;
                        }

                        await importLLMTemplate(projectId, json);
                        alert(`Template "${json.template.name}" erfolgreich importiert!`);
                        e.target.value = ''; // Reset file input
                      } catch (err) {
                        console.error('Import failed:', err);
                        alert('Fehler beim Importieren: ' + (err instanceof Error ? err.message : 'Unbekannter Fehler'));
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
              </div>
              <button
                onClick={() => setShowLLMTemplates(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template erstellen Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingLLMTemplateId ? 'LLM-Template aktualisieren' : 'Als LLM-Template speichern'}
                </h3>
                <p className="text-sm text-gray-500">Template für KI-generierte Post-Serien erstellen</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template-Name *</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="z.B. Januar Fitness-Kampagne"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Wofür wird dieses Template verwendet?"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
                  <select
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value as typeof templateCategory)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="post">Post</option>
                    <option value="story">Story</option>
                    <option value="reel">Reel</option>
                    <option value="carousel">Carousel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template-Typ</label>
                  <select
                    value={templateType}
                    onChange={(e) => setTemplateType(e.target.value as typeof templateType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="campaign">Kampagne</option>
                    <option value="editorial">Editorial</option>
                    <option value="series">Serie</option>
                    <option value="evergreen">Evergreen</option>
                    <option value="seasonal">Saisonal</option>
                  </select>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-purple-900 mb-2">Template-Inhalt</h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Canvas: {canvas.width} x {canvas.height}px</li>
                  <li>• Hintergrund: {canvas.background_type === 'color' ? canvas.background_value : canvas.background_type}</li>
                  <li>• {elements.length} Element{elements.length !== 1 ? 'e' : ''}</li>
                </ul>
                <p className="text-xs text-purple-600 mt-2">
                  Das Template kann später für LLM-Prompt-Generierung verwendet werden.
                </p>
              </div>

              {editingLLMTemplateId && !templateSaveResult && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">Bearbeiten:</span> Änderungen werden am bestehenden Template gespeichert.
                  </p>
                </div>
              )}

              {/* Erfolgs- oder Fehlermeldung */}
              {templateSaveResult && (
                <div className={`border rounded-lg p-4 ${
                  templateSaveResult.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {templateSaveResult.success ? (
                      <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${templateSaveResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {templateSaveResult.success ? 'Template gespeichert!' : 'Fehler'}
                      </p>
                      <p className={`text-sm mt-1 ${templateSaveResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {templateSaveResult.message}
                      </p>
                      {templateSaveResult.displayId && (
                        <div className="mt-3 p-3 bg-white border border-green-200 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Template-ID</p>
                          <code className="text-sm font-mono font-medium text-green-700">
                            {templateSaveResult.displayId}
                          </code>
                        </div>
                      )}
                      {templateSaveResult.success && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-green-600">
                            Das Template ist jetzt im Projekt verfügbar.
                          </p>
                          <button
                            onClick={() => downloadTemplateAsJson(
                              templateName,
                              templateDescription,
                              templateCategory,
                              templateType,
                              templateSaveResult.displayId
                            )}
                            className="w-full px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 flex items-center justify-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Als JSON herunterladen (für LLM)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              {templateSaveResult?.success ? (
                <>
                  <button
                    onClick={() => downloadTemplateAsJson(
                      templateName,
                      templateDescription,
                      templateCategory,
                      templateType,
                      templateSaveResult.displayId
                    )}
                    className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    JSON herunterladen
                  </button>
                  <button
                    onClick={() => {
                      setShowTemplateModal(false);
                      setTemplateSaveResult(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    Schließen
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setTemplateName('');
                    setTemplateDescription('');
                    setTemplateCategory('post');
                    setTemplateType('campaign');
                    setTemplateSaveResult(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Abbrechen
                </button>
              )}
              {!templateSaveResult?.success && (
                <>
                  {/* Nur lokal speichern (ohne Datenbank) */}
                  <button
                    onClick={() => {
                      if (!templateName.trim()) return;
                      downloadTemplateAsJson(
                        templateName,
                        templateDescription,
                        templateCategory,
                        templateType
                      );
                      setTemplateSaveResult({
                        success: true,
                        message: `Template "${templateName}" wurde als JSON-Datei heruntergeladen`,
                      });
                    }}
                    disabled={!templateName.trim()}
                    className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 disabled:opacity-50 flex items-center gap-2"
                    title="Template nur lokal als JSON speichern (ohne Datenbank)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Nur lokal
                  </button>
                  {/* In Datenbank speichern */}
                  <button
                    onClick={async () => {
                      if (!projectId || !templateName.trim()) return;
                      setTemplateSaveResult(null);

                      try {
                        const templateElements = getTemplateElements();

                        if (editingLLMTemplateId) {
                          // Update existing template
                          const updated = await updateLLMTemplate(editingLLMTemplateId, {
                            name: templateName,
                            description: templateDescription || undefined,
                            template_type: templateType,
                            category: templateCategory,
                            canvas,
                            elements: templateElements,
                          });
                          setTemplateSaveResult({
                            success: true,
                            message: `Template "${updated.name}" wurde aktualisiert`,
                            displayId: updated.display_id,
                          });
                        } else {
                          // Create new template
                          const created = await createLLMTemplate({
                            project_id: projectId,
                            name: templateName,
                            description: templateDescription || undefined,
                            template_type: templateType,
                            category: templateCategory,
                            canvas,
                            elements: templateElements,
                          });
                          setTemplateSaveResult({
                            success: true,
                            message: `Template "${created.name}" wurde erstellt`,
                            displayId: created.display_id,
                          });
                        }
                        // Modal bleibt offen - Nutzer sieht Erfolgsmeldung
                      } catch (err) {
                        console.error('Template save failed:', err);
                        setTemplateSaveResult({
                          success: false,
                          message: err instanceof Error ? err.message : 'Fehler beim Speichern des Templates',
                        });
                      }
                    }}
                    disabled={!templateName.trim() || isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Speichern...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    </svg>
                    {editingLLMTemplateId ? 'Aktualisieren' : 'In Datenbank'}
                  </>
                )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Briefing Panel */}
      {showAIBriefing && projectId && (
        <AIBriefingPanel
          projectId={projectId}
          flights={flights}
          onPostsGenerated={(posts) => {
            console.log('Generated posts:', posts);
            // TODO: Navigate to review page or show notification
          }}
          onClose={() => setShowAIBriefing(false)}
        />
      )}

      {/* Create Post from Design Modal */}
      {showCreatePostModal && selectedDesignForPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('creator:createPostFromDesignTitle')}</h3>
                <p className="text-sm text-gray-500">{t('creator:scheduleDesignAsPost')}</p>
              </div>
            </div>

            {/* Design Preview */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-start gap-3">
              {selectedDesignForPost.thumbnail_url ? (
                <img
                  src={getMediaUrl(selectedDesignForPost.thumbnail_url)}
                  alt={selectedDesignForPost.name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              <div className="flex-1">
                <p className="font-medium text-gray-900">{selectedDesignForPost.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                    {selectedDesignForPost.category}
                  </span>
                  <span className="text-xs text-gray-400">
                    {selectedDesignForPost.platform === 'both' ? 'FB+IG' : selectedDesignForPost.platform.toUpperCase().slice(0, 2)}
                  </span>
                  <span className="text-xs text-gray-400">
                    {selectedDesignForPost.canvas.width}x{selectedDesignForPost.canvas.height}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Post Text (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('creator:postText')} <span className="text-gray-400 font-normal">({t('creator:postTextOptional')})</span>
                </label>
                <textarea
                  value={createPostText}
                  onChange={(e) => setCreatePostText(e.target.value)}
                  placeholder={t('creator:captionOrEmpty')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('creator:publishDate')} *
                </label>
                <input
                  type="datetime-local"
                  value={createPostDate}
                  onChange={(e) => setCreatePostDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Flight Selection */}
              {flights.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('creator:flightCampaign')} <span className="text-gray-400 font-normal">({t('creator:postTextOptional')})</span>
                  </label>
                  <select
                    value={createPostFlightId || ''}
                    onChange={(e) => setCreatePostFlightId(e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">{t('creator:noFlight')}</option>
                    {flights.map((flight) => (
                      <option key={flight.id} value={flight.id}>
                        {flight.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Platform Info */}
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p>
                  {t('creator:platformInfo')}: {selectedDesignForPost.platform === 'both' ? 'Facebook & Instagram' :
                    selectedDesignForPost.platform === 'facebook' ? 'Facebook' : 'Instagram'}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreatePostModal(false);
                  setSelectedDesignForPost(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('creator:cancel')}
              </button>
              <button
                onClick={handleCreatePostFromDesign}
                disabled={!createPostDate || isCreatingPost}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isCreatingPost ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('creator:postCreating')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {t('creator:createPostAction')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Recovery Modal */}
      {showRecoveryPrompt && recoveryData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{t('creator:sessionRecoveryTitle')}</h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('creator:unsavedSessionFound')}
                </p>
                {recoveryData.designName && (
                  <p className="mt-2 text-sm text-gray-700">
                    <span className="font-medium">{t('creator:design')}:</span> {recoveryData.designName}
                  </p>
                )}
                {recoveryData.savedAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('creator:savedAt')}: {new Date(recoveryData.savedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleDiscardRecovery}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t('creator:discard')}
              </button>
              <button
                onClick={handleRecoverSession}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('creator:recover')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Import Modal - JSON vom LLM einfügen */}
      {showContentImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Content importieren</h3>
                  <p className="text-sm text-gray-500">JSON vom LLM für &quot;{showContentImport.templateName}&quot;</p>
                </div>
              </div>
              <button
                onClick={() => setShowContentImport(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Tipp:</span> Füge das JSON-Array vom LLM (ChatGPT, Claude) hier ein.
                  Jedes Objekt wird zu einem Bild gerendert.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  JSON-Array einfügen
                </label>
                <textarea
                  value={contentImportJson}
                  onChange={(e) => setContentImportJson(e.target.value)}
                  placeholder={`[
  {
    "content_id": "POST-001",
    "text_1": "Dein Text hier\\nmit Umbrüchen",
    "text_2": "...",
    ...
  },
  {
    "content_id": "POST-002",
    ...
  }
]`}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Vorschau der erkannten Items */}
              {contentImportJson && (() => {
                try {
                  const parsed = JSON.parse(contentImportJson);
                  const items = Array.isArray(parsed) ? parsed : [parsed];
                  return (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800 font-medium mb-2">
                        {items.length} Post{items.length !== 1 ? 's' : ''} erkannt:
                      </p>
                      <div className="space-y-1">
                        {items.slice(0, 5).map((item: Record<string, string>, idx: number) => (
                          <p key={idx} className="text-xs text-green-700 truncate">
                            {item.content_id || `Item ${idx + 1}`}: {item.text_1?.substring(0, 40) || '...'}
                          </p>
                        ))}
                        {items.length > 5 && (
                          <p className="text-xs text-green-600">... und {items.length - 5} weitere</p>
                        )}
                      </div>
                    </div>
                  );
                } catch {
                  if (contentImportJson.trim()) {
                    return (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-700">
                          Ungültiges JSON. Bitte überprüfe die Syntax.
                        </p>
                      </div>
                    );
                  }
                  return null;
                }
              })()}
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex justify-between">
              <button
                onClick={() => setShowContentImport(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Abbrechen
              </button>
              <div className="flex gap-2">
                {/* Als Designs speichern - Hauptaktion */}
                <button
                  onClick={async () => {
                    if (!contentImportJson.trim() || !projectId) return;

                    try {
                      const parsed = JSON.parse(contentImportJson);
                      const items = Array.isArray(parsed) ? parsed : [parsed];

                      // Transform to batch format
                      const batchItems = items.map((item: Record<string, string>) => ({
                        content_id: item.content_id || `item-${Math.random().toString(36).substr(2, 6)}`,
                        content: Object.fromEntries(
                          Object.entries(item).filter(([key]) => key !== 'content_id')
                        ) as Record<string, string>,
                      }));

                      setContentImportLoading(true);
                      const result = await createDesignsFromTemplate(
                        showContentImport.templateId,
                        projectId,
                        batchItems
                      );

                      alert(`${result.count} Designs erstellt! Du findest sie in der Designs-Liste links.`);
                      setShowContentImport(null);
                      setShowLLMTemplates(false);
                      setContentImportJson('');
                      // Refresh saved designs
                      fetchSavedDesigns(projectId);
                    } catch (err) {
                      console.error('Create designs failed:', err);
                      alert('Fehler: ' + (err instanceof Error ? err.message : 'Unbekannter Fehler'));
                    } finally {
                      setContentImportLoading(false);
                    }
                  }}
                  disabled={!contentImportJson.trim() || contentImportLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {contentImportLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Erstelle...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Als Designs speichern
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
